/**
 * ColumnMappingStep — cross-scope column mapping with progressive unlock.
 *
 * The primary scope requires a PK mapping (pre-filled from anchor step, read-only).
 * Secondary scopes unlock when a relation primitive connecting them to an already-
 * unlocked scope is mapped to a document column. Relation mappings sync bidirectionally.
 */

import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import React, { useEffect, useMemo, useReducer, useState } from "react"
import { api } from "../../api/client"
import { useStore } from "../../store/useStore"
import type {
  AnchorableMapping,
  ColumnTypeCheck,
  DetectOut,
  DownloadColumn,
  ParsedSheet,
  PrimitiveDef,
  PrimitiveType,
  ReachablePrimitive,
  Scope,
  UserMapping,
} from "../../types"
import {
  evaluateFormula as evalHelper,
  extractReferences as extractReferencesForEval,
} from "../../utils/formula"
import { AnchorBox, type AnchorMode } from "./AnchorBox"
import { HelperEditor, type HelperRef } from "./HelperEditor"

/** Emitted with the proceed callback so callers can route to the anchor API correctly. */
export interface AnchorChoice {
  mode: AnchorMode
  mappingPrimitiveId: string | null
  mappingColumnIndex: number // -1 if mode === 'pk'
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Configuration for a rollup column that aggregates values from a related scope */
export interface RollupConfig {
  sourceScopeId: string // scope to aggregate FROM (e.g., 'component')
  sourcePrimitiveId: string // primitive to aggregate (resolved at init)
  sourcePrimitiveName: string // primitive name for matching (e.g., 'svhc_gehalt')
  aggregation: "concat" | "concat_unique" | "sum" | "count" // concat = with duplicates, concat_unique = deduplicated
}

/** Configuration for a helper column — a user-authored formula evaluated
 *  per row during the diff + export. Helpers don't persist to the DB; they
 *  materialize only in the diff view and (in export) the downloaded file.
 *  `label` doubles as the helper's bare-identifier so other helpers can
 *  reference it, so it must be a valid JS-style identifier.
 *  `targetDocHeader` (optional) assigns the helper's output to an existing
 *  document column instead of appending a new one — the exported file then
 *  replaces that column's cell with the helper's computed value. */
export interface HelperConfig {
  label: string
  expression: string
  targetDocHeader?: string | null
}

export type MappingRow = {
  primitive_id: string
  primitive_scope_id: string
  primitive_title: string
  primitive_type: string
  column_index: number // -1 = unmapped (import mode)
  related_scope_id?: string | null
  is_mapping_title?: boolean
  typeCheck: ColumnTypeCheck | null
  cleaningInstruction: string
  visible: boolean
  isRollup?: boolean
  isHelper?: boolean // true for helper/formula virtual rows
  disabled: boolean // true when scope is locked — shown but not editable
  label?: string // export mode: user-editable column header name
  rollupConfig?: RollupConfig // set for rollup columns (virtual, not a real primitive)
  helperConfig?: HelperConfig // set for helper columns (virtual, user formula)
  is_pk?: boolean // true for primary key primitives
}

/** Edge in the scope relation graph */
interface ScopeRelationEdge {
  primitiveId: string
  ownerScopeId: string
  targetScopeId: string
  mirrorPrimitiveId: string | null
}

interface ScopeLockState {
  scopeId: string
  unlocked: boolean
  /** The edge that unlocks this scope (null for primary) */
  unlockEdge: ScopeRelationEdge | null
  /** Column index mapped to the unlock relation (-1 = not mapped) */
  relationColumnIndex: number
  hopDistance: number
}

// ---------------------------------------------------------------------------
// Scope topology
// ---------------------------------------------------------------------------

interface ScopeTopology {
  edges: Map<string, ScopeRelationEdge[]>
}

function buildScopeTopology(scopes: Scope[]): ScopeTopology {
  const edges = new Map<string, ScopeRelationEdge[]>()
  for (const scope of scopes) {
    if (scope.is_singleton) continue
    if (!edges.has(scope.id)) edges.set(scope.id, [])
    for (const prim of scope.primitives) {
      if (prim.type !== "relation" || !prim.related_scope_id) continue
      if (!prim.related_scope_id) continue
      // Find mirror relation on the target scope
      const targetScope = scopes.find((s) => s.id === prim.related_scope_id)
      const mirror = targetScope?.primitives.find(
        (p) => p.type === "relation" && p.related_scope_id === scope.id,
      )
      const edge: ScopeRelationEdge = {
        primitiveId: prim.id,
        ownerScopeId: scope.id,
        targetScopeId: prim.related_scope_id,
        mirrorPrimitiveId: mirror?.id ?? null,
      }
      edges.get(scope.id)!.push(edge)
    }
  }
  return { edges }
}

/** BFS from primaryScopeId through only unlocked scopes → returns hop distances */
function computeHopDistances(
  topology: ScopeTopology,
  primaryScopeId: string,
  unlockedScopeIds: Set<string>,
): Map<string, number> {
  const distances = new Map<string, number>()
  distances.set(primaryScopeId, 0)
  const queue = [primaryScopeId]
  while (queue.length > 0) {
    const current = queue.shift()!
    const dist = distances.get(current)!
    for (const edge of topology.edges.get(current) ?? []) {
      if (distances.has(edge.targetScopeId)) continue
      if (!unlockedScopeIds.has(edge.targetScopeId)) continue
      distances.set(edge.targetScopeId, dist + 1)
      queue.push(edge.targetScopeId)
    }
  }
  return distances
}

/** Find the guidance path: what relation mappings are missing to unlock a scope */
function getUnlockGuidance(
  topology: ScopeTopology,
  primaryScopeId: string,
  targetScopeId: string,
  unlockedScopeIds: Set<string>,
  scopeNames: Record<string, string>,
  translate: (
    k: TranslationKeys,
    opts?: Record<string, string | number>,
  ) => string,
): string {
  // BFS from primary through ALL scopes to find shortest path to target
  const parent = new Map<string, string>()
  const queue = [primaryScopeId]
  parent.set(primaryScopeId, "")
  while (queue.length > 0) {
    const current = queue.shift()!
    if (current === targetScopeId) break
    for (const edge of topology.edges.get(current) ?? []) {
      if (parent.has(edge.targetScopeId)) continue
      parent.set(edge.targetScopeId, current)
      queue.push(edge.targetScopeId)
    }
  }
  if (!parent.has(targetScopeId))
    return translate("QMS/INGESTION/NO_COLUMN_MATCH")

  // Walk back to find the first locked scope in the chain
  const chain: string[] = []
  let current = targetScopeId
  while (current && current !== primaryScopeId) {
    chain.unshift(current)
    current = parent.get(current) ?? ""
  }
  chain.unshift(primaryScopeId)

  // Find the first locked link
  for (let i = 1; i < chain.length; i++) {
    if (!unlockedScopeIds.has(chain[i])) {
      const from = scopeNames[chain[i - 1]] ?? chain[i - 1]
      const to = scopeNames[chain[i]] ?? chain[i]
      if (chain[i] === targetScopeId) {
        return translate("QMS/COLUMN_MAPPING/UNLOCK_GUIDANCE_HERE", {
          to,
          from,
        })
      }
      return translate("QMS/COLUMN_MAPPING/UNLOCK_GUIDANCE_CHAIN", {
        to,
        from,
      })
    }
  }
  return ""
}

// ---------------------------------------------------------------------------
// Type validation helpers
// ---------------------------------------------------------------------------

export function checkTypeIncompatible(
  value: string | null,
  primitiveType: PrimitiveType,
): boolean {
  if (!value || value.trim() === "") return false
  if (primitiveType === "string") return false
  if (primitiveType === "relation") return false
  if (primitiveType === "number") {
    const cleaned = value.trim().replace(/,/g, ".")
    return isNaN(Number(cleaned))
  }
  if (primitiveType === "date") {
    return isNaN(Date.parse(value.trim()))
  }
  return false
}

export function validateColumnType(
  rows: string[][],
  columnIndex: number,
  primitiveType: PrimitiveType,
): ColumnTypeCheck & { primitiveId: string; columnHeader: string } {
  const incompatibleSamples: string[] = []
  let incompatibleCount = 0

  for (const row of rows) {
    const val = row[columnIndex]
    if (!val || val.trim() === "") continue
    if (checkTypeIncompatible(val, primitiveType)) {
      incompatibleCount++
      if (incompatibleSamples.length < 3) {
        incompatibleSamples.push(val.trim())
      }
    }
  }

  return {
    primitiveId: "",
    primitiveTitle: "",
    primitiveType,
    columnHeader: "",
    columnIndex,
    compatible: incompatibleCount === 0,
    incompatibleSamples,
    incompatibleCount,
  }
}

// ---------------------------------------------------------------------------
// Reducer state & actions
// ---------------------------------------------------------------------------

interface CrossScopeState {
  primaryScopeId: string
  pkPrimitiveId: string
  pkColumnIndex: number
  /** True when the primary scope's anchor has a valid column assignment —
   *  in PK mode this is pkColumnIndex>=0; in mapping mode it's the anchor
   *  relation row's column_index>=0. Gates all other mappings. */
  pkMapped: boolean
  /** Export mode: no file upload, relations use dummy column_index for unlock */
  isExportMode: boolean
  topology: ScopeTopology
  scopeLocks: Record<string, ScopeLockState>
  mappingRows: MappingRow[]
  activeTab: string
  /** Per-scope detect results keyed by scope ID (lazy-loaded for multi-hop) */
  detectResults: Record<string, DetectOut>
  scopeNames: Record<string, string>
  /** Ordered list of scope IDs by unlock time (primary first, then in order of unlock) */
  unlockOrder: string[]
  /** Primary scope's anchor mode. 'mapping' uses a relation's mapped_id as the
   *  anchor key, auto-unlocks the related scope, and hides the anchor relation
   *  from the primary column-mapping table. */
  anchorMode: AnchorMode
  /** Which relation primitive's mapped_id anchors the primary scope when
   *  anchorMode === 'mapping'. null otherwise. */
  anchorMappingPrimitiveId: string | null
  /** Scope auto-unlocked when this mapping anchor is active — i.e. the side
   *  of the relation opposite the primary scope.  Distinct from the primitive's
   *  own related_scope_id because the primitive may live on the far side
   *  (bi-directional mappings surface from either endpoint). */
  anchorMappingTargetScopeId: string | null
}

type RollupAggregation = "concat" | "concat_unique" | "sum" | "count"

type Action =
  | { type: "REPLACE_STATE"; state: CrossScopeState }
  | { type: "MAP_PK"; columnIndex: number }
  | { type: "MAP_COLUMN"; primitiveId: string; columnIndex: number }
  | {
      type: "MAP_RELATION"
      primitiveId: string
      columnIndex: number
      targetScopeId: string
    }
  | { type: "UNMAP_RELATION"; primitiveId: string; targetScopeId: string }
  | {
      type: "ADD_PRIMITIVE"
      primitive: PrimitiveDef
      detected: DetectOut | null
    }
  | { type: "REMOVE_PRIMITIVE"; primitiveId: string }
  | { type: "TOGGLE_VISIBLE"; primitiveId: string }
  | { type: "TOGGLE_ALL_VISIBLE"; scopeId: string; visible: boolean }
  | { type: "TOGGLE_ROLLUP"; primitiveId: string }
  | {
      type: "SET_CLEANING_INSTRUCTION"
      primitiveId: string
      instruction: string
    }
  | { type: "RENAME_COLUMN"; primitiveId: string; label: string }
  | { type: "SET_ACTIVE_TAB"; scopeId: string }
  | { type: "ADD_SCOPE_DETECT"; scopeId: string; detected: DetectOut }
  | {
      type: "ADD_ROLLUP"
      targetScopeId: string
      sourcePrimitive: PrimitiveDef
      aggregation: RollupAggregation
    }
  | {
      type: "SET_ROLLUP_AGGREGATION"
      primitiveId: string
      aggregation: RollupAggregation
    }
  | { type: "SET_ANCHOR_MODE_PK" }
  | {
      type: "SET_ANCHOR_MODE_MAPPING"
      mappingPrimitiveId: string
      targetScopeId: string
    }
  | { type: "SET_ANCHOR_MAPPING_COLUMN"; columnIndex: number }
  | {
      type: "ADD_HELPER"
      scopeId: string
      label: string
      expression: string
      targetDocHeader?: string | null
    }
  | {
      type: "UPDATE_HELPER"
      primitiveId: string
      label: string
      expression: string
      targetDocHeader?: string | null
    }

function recalcLocks(state: CrossScopeState): CrossScopeState {
  const anchorRow =
    state.anchorMode === "mapping" && state.anchorMappingPrimitiveId
      ? state.mappingRows.find(
          (r) => r.primitive_id === state.anchorMappingPrimitiveId,
        )
      : null
  const mappingAnchorReady = !!anchorRow && anchorRow.column_index >= 0
  const pkMapped =
    state.isExportMode ||
    (state.anchorMode === "mapping"
      ? mappingAnchorReady
      : state.pkColumnIndex >= 0)

  // If the primary anchor is not mapped, everything except the anchor row
  // itself is disabled — in PK mode that's the PK primitive, in mapping mode
  // it's the chosen relation primitive.
  if (!pkMapped) {
    const anchorRowId =
      state.anchorMode === "mapping"
        ? state.anchorMappingPrimitiveId
        : state.pkPrimitiveId
    const newLocks: Record<string, ScopeLockState> = {}
    for (const [scopeId, lock] of Object.entries(state.scopeLocks)) {
      newLocks[scopeId] = {
        ...lock,
        unlocked: scopeId === state.primaryScopeId,
        hopDistance: scopeId === state.primaryScopeId ? 0 : 999,
      }
    }
    const newRows = state.mappingRows.map((r) => ({
      ...r,
      disabled: r.primitive_id !== anchorRowId,
    }))
    return { ...state, pkMapped, scopeLocks: newLocks, mappingRows: newRows }
  }

  const unlockedIds = new Set<string>()
  unlockedIds.add(state.primaryScopeId)

  // A secondary scope is unlocked if its relation mapping has a column assigned
  for (const [scopeId, lock] of Object.entries(state.scopeLocks)) {
    if (scopeId === state.primaryScopeId) {
      unlockedIds.add(scopeId)
      continue
    }
    if (lock.relationColumnIndex >= 0) {
      unlockedIds.add(scopeId)
    }
  }

  // BFS to verify reachability through unlocked chain
  const reachable = computeHopDistances(
    state.topology,
    state.primaryScopeId,
    unlockedIds,
  )
  const reachableIds = new Set(reachable.keys())

  // Only scopes reachable via unlocked chain are actually unlocked
  const newLocks = { ...state.scopeLocks }
  // Track newly unlocked scopes for unlock order
  const newlyUnlocked: string[] = []
  for (const [scopeId, lock] of Object.entries(newLocks)) {
    if (scopeId === state.primaryScopeId) {
      newLocks[scopeId] = { ...lock, unlocked: true, hopDistance: 0 }
      continue
    }
    const isReachable =
      reachableIds.has(scopeId) && lock.relationColumnIndex >= 0
    const wasUnlocked = lock.unlocked
    newLocks[scopeId] = {
      ...lock,
      unlocked: isReachable,
      hopDistance: reachable.get(scopeId) ?? 999,
    }
    if (isReachable && !wasUnlocked) {
      newlyUnlocked.push(scopeId)
    }
  }

  // Update unlock order: append newly unlocked, remove re-locked
  let unlockOrder = [...state.unlockOrder]
  // Remove scopes that are no longer unlocked
  unlockOrder = unlockOrder.filter(
    (sid) => sid === state.primaryScopeId || newLocks[sid]?.unlocked,
  )
  // Append newly unlocked scopes
  for (const sid of newlyUnlocked) {
    if (!unlockOrder.includes(sid)) unlockOrder.push(sid)
  }

  // Update disabled state on mapping rows
  const newRows = state.mappingRows.map((r) => {
    if (r.primitive_id === state.pkPrimitiveId) return { ...r, disabled: false }
    if (r.primitive_scope_id === state.primaryScopeId)
      return { ...r, disabled: false }
    const lock = newLocks[r.primitive_scope_id]
    return { ...r, disabled: !lock?.unlocked }
  })

  return {
    ...state,
    pkMapped,
    scopeLocks: newLocks,
    mappingRows: newRows,
    unlockOrder,
  }
}

function reducer(state: CrossScopeState, action: Action): CrossScopeState {
  switch (action.type) {
    case "REPLACE_STATE":
      return action.state

    case "MAP_PK": {
      // Update PK column assignment — triggers lock/unlock of everything
      const newRows = state.mappingRows.map((r) => {
        if (r.primitive_id !== state.pkPrimitiveId) return r
        return { ...r, column_index: action.columnIndex, typeCheck: null }
      })
      return recalcLocks({
        ...state,
        pkColumnIndex: action.columnIndex,
        mappingRows: newRows,
      })
    }

    case "MAP_COLUMN": {
      const newRows = state.mappingRows.map((r) => {
        if (r.primitive_id !== action.primitiveId) return r
        if (r.disabled) return r
        const check =
          r.primitive_type === "relation"
            ? null
            : action.columnIndex < 0
              ? null
              : undefined
        return {
          ...r,
          column_index: action.columnIndex,
          typeCheck: check === undefined ? r.typeCheck : check,
        }
      })
      return { ...state, mappingRows: newRows }
    }

    case "MAP_RELATION": {
      // Update the relation row in the owner scope
      let newRows = state.mappingRows.map((r) => {
        if (r.primitive_id !== action.primitiveId) return r
        return { ...r, column_index: action.columnIndex, typeCheck: null }
      })

      // Find the mirror relation and sync it
      const ownerRow = newRows.find(
        (r) => r.primitive_id === action.primitiveId,
      )
      if (ownerRow) {
        const edges =
          state.topology.edges.get(ownerRow.primitive_scope_id) ?? []
        const edge = edges.find((e) => e.primitiveId === action.primitiveId)
        if (edge?.mirrorPrimitiveId) {
          const mirrorExists = newRows.some(
            (r) => r.primitive_id === edge.mirrorPrimitiveId,
          )
          if (mirrorExists) {
            newRows = newRows.map((r) => {
              if (r.primitive_id !== edge.mirrorPrimitiveId) return r
              return { ...r, column_index: action.columnIndex, typeCheck: null }
            })
          }
        }
      }

      // Update scope lock
      const newLocks = { ...state.scopeLocks }
      if (newLocks[action.targetScopeId]) {
        newLocks[action.targetScopeId] = {
          ...newLocks[action.targetScopeId],
          relationColumnIndex: action.columnIndex,
        }
      } else {
        // Find the edge info for this relation
        const allEdges = [...state.topology.edges.values()].flat()
        const edge = allEdges.find((e) => e.primitiveId === action.primitiveId)
        newLocks[action.targetScopeId] = {
          scopeId: action.targetScopeId,
          unlocked: false, // will be recalculated
          unlockEdge: edge ?? null,
          relationColumnIndex: action.columnIndex,
          hopDistance: 999,
        }
      }

      return recalcLocks({
        ...state,
        mappingRows: newRows,
        scopeLocks: newLocks,
      })
    }

    case "UNMAP_RELATION": {
      // Set column to -1 on the relation row
      let newRows = state.mappingRows.map((r) => {
        if (r.primitive_id !== action.primitiveId) return r
        return { ...r, column_index: -1, typeCheck: null }
      })

      // Sync mirror
      const ownerRow = state.mappingRows.find(
        (r) => r.primitive_id === action.primitiveId,
      )
      if (ownerRow) {
        const edges =
          state.topology.edges.get(ownerRow.primitive_scope_id) ?? []
        const edge = edges.find((e) => e.primitiveId === action.primitiveId)
        if (edge?.mirrorPrimitiveId) {
          newRows = newRows.map((r) => {
            if (r.primitive_id !== edge.mirrorPrimitiveId) return r
            return { ...r, column_index: -1, typeCheck: null }
          })
        }
      }

      // Reset scope lock
      const newLocks = { ...state.scopeLocks }
      if (newLocks[action.targetScopeId]) {
        newLocks[action.targetScopeId] = {
          ...newLocks[action.targetScopeId],
          relationColumnIndex: -1,
        }
      }

      return recalcLocks({
        ...state,
        mappingRows: newRows,
        scopeLocks: newLocks,
      })
    }

    case "ADD_PRIMITIVE": {
      const p = action.primitive
      const detected = action.detected
      // In export mode, relations are structural (no file columns) — use dummy index 0
      const isRelation = p.type === "relation" && !!p.related_scope_id
      const colIdx = (() => {
        if (state.isExportMode && isRelation) return 0
        if (detected) {
          const expl = detected.explicit.find((e) => e.primitive_id === p.id)
          if (expl) return expl.column_index
          const impl = detected.implicit.find((c) => c.primitive_id === p.id)
          if (impl?.best_match_column_index != null)
            return impl.best_match_column_index
        }
        return -1
      })()
      const newRow: MappingRow = {
        primitive_id: p.id,
        primitive_scope_id: p.scope_id,
        primitive_title: p.title,
        primitive_type: p.type,
        column_index: colIdx,
        related_scope_id: p.related_scope_id ?? null,
        is_mapping_title: false,
        typeCheck: null,
        cleaningInstruction: "",
        visible: true,
        isRollup: false,
        disabled: false,
      }
      const newState = { ...state, mappingRows: [...state.mappingRows, newRow] }

      // If this is a relation primitive and has a column, treat as MAP_RELATION
      if (isRelation && colIdx >= 0) {
        return reducer(newState, {
          type: "MAP_RELATION",
          primitiveId: p.id,
          columnIndex: colIdx,
          targetScopeId: p.related_scope_id!,
        })
      }
      return newState
    }

    case "REMOVE_PRIMITIVE": {
      // Don't remove PK
      if (action.primitiveId === state.pkPrimitiveId) return state
      const row = state.mappingRows.find(
        (r) => r.primitive_id === action.primitiveId,
      )
      if (!row) return state

      // If it's a relation with a column, unmap first
      if (
        row.primitive_type === "relation" &&
        row.related_scope_id &&
        row.column_index >= 0
      ) {
        const afterUnmap = reducer(state, {
          type: "UNMAP_RELATION",
          primitiveId: action.primitiveId,
          targetScopeId: row.related_scope_id,
        })
        return {
          ...afterUnmap,
          mappingRows: afterUnmap.mappingRows.filter(
            (r) => r.primitive_id !== action.primitiveId,
          ),
        }
      }

      return {
        ...state,
        mappingRows: state.mappingRows.filter(
          (r) => r.primitive_id !== action.primitiveId,
        ),
      }
    }

    case "TOGGLE_VISIBLE": {
      if (action.primitiveId === state.pkPrimitiveId) return state
      return {
        ...state,
        mappingRows: state.mappingRows.map((r) =>
          r.primitive_id === action.primitiveId
            ? { ...r, visible: !r.visible }
            : r,
        ),
      }
    }

    case "TOGGLE_ALL_VISIBLE": {
      return {
        ...state,
        mappingRows: state.mappingRows.map((r) =>
          r.primitive_scope_id === action.scopeId
            ? {
                ...r,
                visible:
                  r.primitive_id === state.pkPrimitiveId
                    ? true
                    : action.visible,
              }
            : r,
        ),
      }
    }

    case "TOGGLE_ROLLUP": {
      return {
        ...state,
        mappingRows: state.mappingRows.map((r) =>
          r.primitive_id === action.primitiveId
            ? { ...r, isRollup: !r.isRollup }
            : r,
        ),
      }
    }

    case "SET_CLEANING_INSTRUCTION": {
      return {
        ...state,
        mappingRows: state.mappingRows.map((r) =>
          r.primitive_id === action.primitiveId
            ? { ...r, cleaningInstruction: action.instruction }
            : r,
        ),
      }
    }

    case "RENAME_COLUMN": {
      return {
        ...state,
        mappingRows: state.mappingRows.map((r) =>
          r.primitive_id === action.primitiveId
            ? { ...r, label: action.label }
            : r,
        ),
      }
    }

    case "SET_ACTIVE_TAB": {
      return { ...state, activeTab: action.scopeId }
    }

    case "ADD_SCOPE_DETECT": {
      return {
        ...state,
        detectResults: {
          ...state.detectResults,
          [action.scopeId]: action.detected,
        },
      }
    }

    case "ADD_ROLLUP": {
      const p = action.sourcePrimitive
      const syntheticId = `_rollup_${p.scope_id}_${p.name}`
      // Prevent duplicate rollup rows
      if (state.mappingRows.some((r) => r.primitive_id === syntheticId))
        return state
      const newRow: MappingRow = {
        primitive_id: syntheticId,
        primitive_scope_id: action.targetScopeId,
        primitive_title: p.title,
        primitive_type: p.type,
        column_index: -1,
        related_scope_id: null,
        is_mapping_title: false,
        typeCheck: null,
        cleaningInstruction: "",
        visible: true,
        isRollup: true,
        disabled: false,
        label: p.title,
        is_pk: false,
        rollupConfig: {
          sourceScopeId: p.scope_id,
          sourcePrimitiveId: p.id,
          sourcePrimitiveName: p.name,
          aggregation: action.aggregation,
        },
      }
      return { ...state, mappingRows: [...state.mappingRows, newRow] }
    }

    case "SET_ROLLUP_AGGREGATION": {
      return {
        ...state,
        mappingRows: state.mappingRows.map((r) =>
          r.primitive_id === action.primitiveId && r.rollupConfig
            ? {
                ...r,
                rollupConfig: {
                  ...r.rollupConfig,
                  aggregation: action.aggregation,
                },
              }
            : r,
        ),
      }
    }

    case "SET_ANCHOR_MODE_PK": {
      // Tear down any existing mapping anchor: unmap its relation (which
      // re-locks the related scope via recalcLocks) and silently discard
      // every mapping row that lived on that scope.
      let s: CrossScopeState = state
      const oldId = state.anchorMappingPrimitiveId
      const oldTarget = state.anchorMappingTargetScopeId
      if (oldId && oldTarget) {
        s = reducer(s, {
          type: "UNMAP_RELATION",
          primitiveId: oldId,
          targetScopeId: oldTarget,
        })
        s = {
          ...s,
          mappingRows: s.mappingRows.filter(
            (r) => r.primitive_scope_id !== oldTarget,
          ),
        }
        s = recalcLocks(s)
      }
      return {
        ...s,
        anchorMode: "pk",
        anchorMappingPrimitiveId: null,
        anchorMappingTargetScopeId: null,
      }
    }

    case "SET_ANCHOR_MODE_MAPPING": {
      // Switching INTO mapping mode from another mapping primitive: tear down
      // the previous one first so only the newly-selected relation is the
      // anchor (prevents stale unlocks when the user cycles through options).
      let s: CrossScopeState = state
      const oldId = state.anchorMappingPrimitiveId
      const oldTarget = state.anchorMappingTargetScopeId
      if (oldId && oldId !== action.mappingPrimitiveId && oldTarget) {
        s = reducer(s, {
          type: "UNMAP_RELATION",
          primitiveId: oldId,
          targetScopeId: oldTarget,
        })
        s = {
          ...s,
          mappingRows: s.mappingRows.filter(
            (r) => r.primitive_scope_id !== oldTarget,
          ),
        }
      }
      // Switching from PK to Mapping: clear the PK column assignment so the
      // dropdown in the AnchorBox doesn't block the user from picking the
      // same CSV column as the mapping anchor.  The PK primitive stays in
      // state.mappingRows; only its column_index is reset to -1.
      s = {
        ...s,
        pkColumnIndex: -1,
        mappingRows: s.mappingRows.map((r) =>
          r.primitive_id === state.pkPrimitiveId
            ? { ...r, column_index: -1, typeCheck: null }
            : r,
        ),
      }
      // Flag the anchor relation row as a mapping-title column so backend
      // extract-explicit resolves the edge by mapped_id rather than PK.
      s = {
        ...s,
        mappingRows: s.mappingRows.map((r) =>
          r.primitive_id === action.mappingPrimitiveId
            ? { ...r, is_mapping_title: true }
            : r,
        ),
      }
      s = recalcLocks({
        ...s,
        anchorMode: "mapping",
        anchorMappingPrimitiveId: action.mappingPrimitiveId,
        anchorMappingTargetScopeId: action.targetScopeId,
      })
      return s
    }

    case "SET_ANCHOR_MAPPING_COLUMN": {
      const primId = state.anchorMappingPrimitiveId
      const target = state.anchorMappingTargetScopeId
      if (!primId || !target) return state
      if (action.columnIndex < 0) {
        return reducer(state, {
          type: "UNMAP_RELATION",
          primitiveId: primId,
          targetScopeId: target,
        })
      }
      return reducer(state, {
        type: "MAP_RELATION",
        primitiveId: primId,
        columnIndex: action.columnIndex,
        targetScopeId: target,
      })
    }

    case "ADD_HELPER": {
      const id = `_helper_${action.scopeId}_${action.label}_${Date.now()}`
      const newRow: MappingRow = {
        primitive_id: id,
        primitive_scope_id: action.scopeId,
        primitive_title: action.label,
        primitive_type: "formula",
        column_index: -1,
        related_scope_id: null,
        is_mapping_title: false,
        typeCheck: null,
        cleaningInstruction: "",
        visible: true,
        isRollup: false,
        isHelper: true,
        disabled: false,
        label: action.label,
        helperConfig: {
          label: action.label,
          expression: action.expression,
          targetDocHeader: action.targetDocHeader ?? null,
        },
      }
      return { ...state, mappingRows: [...state.mappingRows, newRow] }
    }

    case "UPDATE_HELPER": {
      return {
        ...state,
        mappingRows: state.mappingRows.map((r) =>
          r.primitive_id === action.primitiveId && r.isHelper
            ? {
                ...r,
                primitive_title: action.label,
                label: action.label,
                helperConfig: {
                  label: action.label,
                  expression: action.expression,
                  targetDocHeader: action.targetDocHeader ?? null,
                },
              }
            : r,
        ),
      }
    }

    default:
      return state
  }
}

// ---------------------------------------------------------------------------
// Init state builder
// ---------------------------------------------------------------------------

function buildInitialState(
  primaryScopeId: string,
  detected: DetectOut,
  topology: ScopeTopology,
  pkPrimitiveId: string,
  pkPrimitiveTitle: string,
  pkPrimitiveType: string,
  isExportMode: boolean,
  headers: string[],
  presetMappings: UserMapping[] | undefined,
  scopeNames: Record<string, string>,
): CrossScopeState {
  // Build initial mapping rows from detect results (exclude formula — computed, not imported)
  const explicitRows: MappingRow[] = detected.explicit
    .filter((e) => e.primitive_type !== "formula")
    .map((e) => ({
      primitive_id: e.primitive_id,
      primitive_scope_id: e.primitive_scope_id,
      primitive_title: e.primitive_title,
      primitive_type: e.primitive_type,
      column_index: e.column_index,
      related_scope_id: e.related_scope_id ?? null,
      is_mapping_title: e.is_mapping_title ?? false,
      typeCheck: null,
      cleaningInstruction: "",
      visible: true,
      isRollup: false,
      disabled: e.primitive_scope_id !== primaryScopeId,
    }))

  // Ensure PK is present
  if (pkPrimitiveId) {
    const hasPk = explicitRows.some((r) => r.primitive_id === pkPrimitiveId)
    if (!hasPk) {
      const pkDetect = detected.explicit.find(
        (e) => e.primitive_id === pkPrimitiveId,
      )
      let pkColIdx = pkDetect?.column_index ?? -1
      if (pkColIdx < 0 && headers.length > 0) {
        const usedCols = new Set(
          explicitRows.map((r) => r.column_index).filter((i) => i >= 0),
        )
        pkColIdx = headers.findIndex((_, i) => !usedCols.has(i))
        if (pkColIdx < 0) pkColIdx = 0
      }
      explicitRows.unshift({
        primitive_id: pkPrimitiveId,
        primitive_scope_id: primaryScopeId,
        primitive_title: pkPrimitiveTitle,
        primitive_type: pkPrimitiveType,
        column_index: pkColIdx,
        related_scope_id: null,
        is_mapping_title: false,
        typeCheck: null,
        cleaningInstruction: "",
        visible: true,
        isRollup: false,
        disabled: false,
      })
    }
  }

  // Apply presets
  const rows = explicitRows.map((r) => {
    if (pkPrimitiveId && r.primitive_id === pkPrimitiveId) return r
    if (presetMappings && presetMappings.length > 0) {
      const preset = presetMappings.find(
        (p) => p.primitive_id === r.primitive_id,
      )
      if (preset) return { ...r, column_index: preset.column_index }
    }
    return r
  })

  // Build initial scope locks from topology
  const scopeLocks: Record<string, ScopeLockState> = {}
  scopeLocks[primaryScopeId] = {
    scopeId: primaryScopeId,
    unlocked: true,
    unlockEdge: null,
    relationColumnIndex: -1,
    hopDistance: 0,
  }

  // For each scope reachable in the topology, create a lock entry
  const allScopeIds = new Set<string>()
  for (const [sid, edges] of topology.edges) {
    allScopeIds.add(sid)
    for (const e of edges) allScopeIds.add(e.targetScopeId)
  }
  for (const sid of allScopeIds) {
    if (sid === primaryScopeId) continue
    // Find the relation edge that connects this scope to the primary (direct or indirect)
    const edges = topology.edges.get(sid) ?? []
    const edgeToPrimary = edges.find((e) => e.targetScopeId === primaryScopeId)
    // Check if there's already a mapping row for a relation pointing to this scope
    const relationRow = rows.find(
      (r) =>
        r.primitive_type === "relation" &&
        r.related_scope_id === sid &&
        r.column_index >= 0,
    )
    scopeLocks[sid] = {
      scopeId: sid,
      unlocked: false,
      unlockEdge: edgeToPrimary ?? null,
      relationColumnIndex: relationRow?.column_index ?? -1,
      hopDistance: 999,
    }
  }

  const pkColIdx =
    rows.find((r) => r.primitive_id === pkPrimitiveId)?.column_index ?? -1

  const initialState: CrossScopeState = {
    primaryScopeId,
    pkPrimitiveId,
    pkColumnIndex: pkColIdx,
    pkMapped: pkColIdx >= 0,
    isExportMode,
    topology,
    scopeLocks,
    mappingRows: rows,
    activeTab: primaryScopeId,
    detectResults: { [primaryScopeId]: detected },
    scopeNames,
    unlockOrder: [primaryScopeId],
    anchorMode: "pk",
    anchorMappingPrimitiveId: null,
    anchorMappingTargetScopeId: null,
  }

  return recalcLocks(initialState)
}

// ---------------------------------------------------------------------------
// ColumnMappingStep Component
// ---------------------------------------------------------------------------

export function ColumnMappingStep({
  scopeId,
  mode = "import",
  // Import mode props
  sheet,
  detected,
  pkPrimitiveId,
  pkPrimitiveTitle,
  pkPrimitiveType,
  pkOptional,
  pkValue,
  presetMappings,
  onProceed,
  // Export mode props
  onExportProceed,
  selectedRecordIds,
  selectedCount,
  exportFormatState,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onExportFormatChange,
  readOnly,
  isExportFlow,
  exportColumnFilter,
  exportRelationChain,
}: {
  scopeId: string
  mode?: "import" | "export"
  // Import mode
  sheet?: ParsedSheet | null
  detected?: DetectOut
  pkPrimitiveId?: string | null
  pkPrimitiveTitle?: string
  pkPrimitiveType?: string
  pkOptional?: boolean
  pkValue?: string
  presetMappings?: UserMapping[]
  onProceed?: (mappings: UserMapping[], anchorChoice: AnchorChoice) => void
  // Export mode
  onExportProceed?: (
    columns: import("../../types").DownloadColumn[],
    format: string,
  ) => void
  selectedRecordIds?: Set<string>
  selectedCount?: number
  exportFormatState?: import("../../types").ExportFormat
  onExportFormatChange?: (fmt: import("../../types").ExportFormat) => void
  /** When true, all column editing is disabled (template mode) */
  readOnly?: boolean
  /** True when the host flow (upload-based export wizard) is producing an
   *  export document even though this component still runs in import mode
   *  visually.  Controls only the Rollup/Rolldown affordance. */
  isExportFlow?: boolean
  /** Optional filter for export columns: only include matching (scope_id, primitive_name) pairs.
   *  When label is provided, it overrides the primitive title as the column header.
   *  When rollup_from is provided, this is a virtual rollup column (not a real primitive). */
  exportColumnFilter?: {
    scope_id: string
    primitive_name: string
    alt_names?: string[]
    label?: string
    /** i18n key override for the column label, resolved via translate(). */
    labelKey?: string
    rollup_from?: {
      source_scope_id: string
      source_primitive_name: string
      alt_names?: string[]
      aggregation: "concat" | "concat_unique" | "sum" | "count"
    }
    /** Preset a relation primitive as a mapping-title row (same shape the
     *  wizard produces when the user picks a mapping anchor). */
    is_mapping_title?: boolean
    /** Preset a helper/formula row (same shape ADD_HELPER would produce). */
    helperConfig?: { label: string; expression: string }
  }[]
  /** Pre-configured relation chain for template mode (auto-unlocks scopes along the chain) */
  exportRelationChain?: {
    from_scope: string
    relation_name: string
    to_scope: string
  }[]
}) {
  const { translate } = useTranslation()
  const isExportMode = mode === "export"
  const headers = sheet?.headers ?? []
  const rows = sheet?.rows ?? []
  const previewRows = rows.slice(0, 3)

  // Fetch all scopes (with primitives) for topology + tab navigation
  const { data: allScopesListData, isLoading: allScopesLoading } = useQuery({
    queryKey: ["scopes-list"],
    queryFn: () => api.listScopes(),
  })
  const isHiddenScope = (s: { id: string; is_singleton?: boolean }) =>
    s.is_singleton === true
  const allScopesList = (allScopesListData ?? []).filter(
    (s) => !isHiddenScope(s),
  )

  // Fetch full scope definitions for topology building
  const scopeQueries = allScopesList.map((s) => s.id)
  const { data: fullScopes } = useQuery({
    queryKey: ["scopes-full", scopeQueries.sort().join(",")],
    queryFn: async () => {
      const results = await Promise.all(
        scopeQueries.map((id) => api.getScope(id)),
      )
      return results
    },
    enabled: scopeQueries.length > 0,
  })

  const scopeNames = useMemo(() => {
    const map: Record<string, string> = {}
    for (const s of allScopesList) map[s.id] = s.name
    return map
  }, [allScopesList])

  // Build topology once
  const topology = useMemo(
    () => buildScopeTopology(fullScopes ?? []),
    [fullScopes],
  )

  // Export mode: fetch available columns
  const [exportAvailable, setExportAvailable] = useState<ReachablePrimitive[]>(
    [],
  )
  const [exportLoading, setExportLoading] = useState(isExportMode)
  useEffect(() => {
    if (!isExportMode) return
    ;(async () => {
      try {
        const cols = await api.exportAvailableColumns(scopeId)
        setExportAvailable(cols)
      } catch (e) {
        console.error("Failed to load export columns:", e)
      } finally {
        setExportLoading(false)
      }
    })()
  }, [isExportMode, scopeId])

  // Reducer state
  const emptyDetect: DetectOut = { explicit: [], implicit: [] }
  const [state, dispatch] = useReducer(reducer, undefined, () => {
    if (isExportMode) {
      // Export mode starts with empty state — populated via useEffect when exportAvailable loads
      return buildInitialState(
        scopeId,
        emptyDetect,
        topology,
        "",
        "",
        "",
        isExportMode,
        [],
        undefined,
        scopeNames,
      )
    }
    if (!pkPrimitiveId || pkOptional) {
      return buildInitialState(
        scopeId,
        detected ?? emptyDetect,
        topology,
        pkPrimitiveId ?? "",
        pkPrimitiveTitle ?? "",
        pkPrimitiveType ?? "",
        false,
        headers,
        presetMappings,
        scopeNames,
      )
    }
    return buildInitialState(
      scopeId,
      detected ?? emptyDetect,
      topology,
      pkPrimitiveId,
      pkPrimitiveTitle ?? "",
      pkPrimitiveType ?? "",
      false,
      headers,
      presetMappings,
      scopeNames,
    )
  })

  // Re-initialize when topology loads (import mode) or export columns load
  const topologyReady = (fullScopes?.length ?? 0) > 0
  const [initialized, setInitialized] = useState(false)

  // Single unified init effect: fires when the required data is available
  useEffect(() => {
    if (initialized) return

    if (isExportMode) {
      // Export mode: init when exportAvailable loads.
      // Always wait for topology so scopeLocks covers all scopes (needed for cross-scope unlock).
      if (exportLoading || exportAvailable.length === 0) return
      if (!topologyReady) return
      setInitialized(true)

      // Split the preset into four kinds of column definitions so each one
      // can build the MappingRow shape the wizard uses when a user adds it
      // manually (regular primitive, rollup, helper/formula, mapping-title
      // relation).  Ordering below preserves the preset order.
      const regularFilters =
        exportColumnFilter?.filter(
          (f) => !f.rollup_from && !f.helperConfig && !f.is_mapping_title,
        ) ?? []
      const rollupFilters =
        exportColumnFilter?.filter((f) => f.rollup_from) ?? []
      const helperFilters =
        exportColumnFilter?.filter((f) => f.helperConfig) ?? []
      const mappingTitleFilters =
        exportColumnFilter?.filter((f) => f.is_mapping_title) ?? []

      // Match helper: check primitive_name OR alt_names
      const nameMatches = (
        filter: { primitive_name: string; alt_names?: string[] },
        primName: string,
      ) =>
        filter.primitive_name === primName ||
        (filter.alt_names?.includes(primName) ?? false)

      const filtered = exportColumnFilter
        ? exportAvailable.filter((c) => {
            if (c.primitive_type === "relation") return false
            return regularFilters.some(
              (f) =>
                f.scope_id === c.scope_id && nameMatches(f, c.primitive_name),
            )
          })
        : exportAvailable.filter(
            (c) => c.is_default && c.primitive_type !== "relation",
          )

      const exportRows: MappingRow[] = filtered.map((c) => {
        const filterEntry = regularFilters.find(
          (f) => f.scope_id === c.scope_id && nameMatches(f, c.primitive_name),
        )
        return {
          primitive_id: c.primitive_id,
          primitive_scope_id: c.scope_id,
          primitive_title: c.primitive_title,
          primitive_type: c.primitive_type,
          column_index: -1,
          related_scope_id: null,
          is_mapping_title: false,
          typeCheck: null,
          cleaningInstruction: "",
          visible: true,
          isRollup: false,
          disabled: false,
          label: filterEntry?.labelKey
            ? translate(filterEntry.labelKey as TranslationKeys)
            : (filterEntry?.label ?? c.primitive_title),
          is_pk: c.is_pk,
        }
      })

      // Add rollup MappingRows
      for (const rf of rollupFilters) {
        if (!rf.rollup_from) continue
        const rollupFrom = rf.rollup_from
        const rollupCandidateNames = [
          rollupFrom.source_primitive_name,
          ...(rollupFrom.alt_names ?? []),
        ]

        // Resolve source primitive: try each candidate name against exportAvailable, then fullScopes
        let resolvedPrimId = ""
        let resolvedTitle: string | undefined
        let resolvedType: string | undefined
        let resolvedName = rollupFrom.source_primitive_name

        for (const candidateName of rollupCandidateNames) {
          const fromAvail = exportAvailable.find(
            (c) =>
              c.scope_id === rollupFrom.source_scope_id &&
              c.primitive_name === candidateName,
          )
          if (fromAvail) {
            resolvedPrimId = fromAvail.primitive_id
            resolvedTitle = fromAvail.primitive_title
            resolvedType = fromAvail.primitive_type
            resolvedName = candidateName
            break
          }
        }
        if (!resolvedPrimId && fullScopes) {
          const srcScope = fullScopes.find(
            (s) => s.id === rollupFrom.source_scope_id,
          )
          if (srcScope) {
            for (const candidateName of rollupCandidateNames) {
              const srcPrim = srcScope.primitives.find(
                (p) => p.name === candidateName,
              )
              if (srcPrim) {
                resolvedPrimId = srcPrim.id
                resolvedTitle = resolvedTitle ?? srcPrim.title
                resolvedType = resolvedType ?? srcPrim.type
                resolvedName = candidateName
                break
              }
            }
          }
        }
        exportRows.push({
          primitive_id: `_rollup_${rollupFrom.source_scope_id}_${resolvedName}`,
          primitive_scope_id: rf.scope_id,
          primitive_title:
            rf.label ?? resolvedTitle ?? rollupFrom.source_primitive_name,
          primitive_type: resolvedType ?? "string",
          column_index: -1,
          related_scope_id: null,
          is_mapping_title: false,
          typeCheck: null,
          cleaningInstruction: "",
          visible: true,
          isRollup: true,
          disabled: false,
          label: rf.label ?? resolvedTitle ?? rollupFrom.source_primitive_name,
          is_pk: false,
          rollupConfig: {
            sourceScopeId: rollupFrom.source_scope_id,
            sourcePrimitiveId: resolvedPrimId,
            sourcePrimitiveName: resolvedName,
            aggregation: rollupFrom.aggregation,
          },
        })
      }

      // Helper/formula columns — synthetic virtual rows matching the
      // ADD_HELPER reducer's shape so user-added helpers and template
      // presets look identical downstream (isHelper: true, helperConfig).
      for (const hf of helperFilters) {
        if (!hf.helperConfig) continue
        const helperLabel = hf.label ?? hf.helperConfig.label
        exportRows.push({
          primitive_id: `_helper_${hf.scope_id}_${hf.helperConfig.label}`,
          primitive_scope_id: hf.scope_id,
          primitive_title: helperLabel,
          primitive_type: "formula",
          column_index: -1,
          related_scope_id: null,
          is_mapping_title: false,
          typeCheck: null,
          cleaningInstruction: "",
          visible: true,
          isRollup: false,
          isHelper: true,
          disabled: false,
          label: helperLabel,
          is_pk: false,
          helperConfig: {
            label: hf.helperConfig.label,
            expression: hf.helperConfig.expression,
            targetDocHeader: null,
          },
        })
      }

      const newState = buildInitialState(
        scopeId,
        emptyDetect,
        topology,
        "",
        "",
        "",
        true,
        [],
        undefined,
        scopeNames,
      )
      newState.mappingRows = exportRows
      newState.pkMapped = true
      newState.pkColumnIndex = 0

      // Pre-unlock relation chain (uses fullScopes if available, otherwise skips —
      // relations are cosmetic in readOnly/template mode)
      if (exportRelationChain && fullScopes) {
        for (const rel of exportRelationChain) {
          const fromScope = fullScopes.find((s) => s.id === rel.from_scope)
          const relPrim = fromScope?.primitives.find(
            (p) => p.name === rel.relation_name && p.type === "relation",
          )
          if (!relPrim) continue
          const toScope = fullScopes.find((s) => s.id === rel.to_scope)
          const mirrorPrim = toScope?.primitives.find(
            (p) =>
              p.type === "relation" && p.related_scope_id === rel.from_scope,
          )

          newState.mappingRows.push({
            primitive_id: relPrim.id,
            primitive_scope_id: rel.from_scope,
            primitive_title: relPrim.title,
            primitive_type: "relation",
            column_index: 0,
            related_scope_id: rel.to_scope,
            is_mapping_title: false,
            typeCheck: null,
            cleaningInstruction: "",
            visible: true,
            isRollup: false,
            disabled: false,
            label: relPrim.title,
            is_pk: false,
          })
          if (mirrorPrim) {
            newState.mappingRows.push({
              primitive_id: mirrorPrim.id,
              primitive_scope_id: rel.to_scope,
              primitive_title: mirrorPrim.title,
              primitive_type: "relation",
              column_index: 0,
              related_scope_id: rel.from_scope,
              is_mapping_title: false,
              typeCheck: null,
              cleaningInstruction: "",
              visible: true,
              isRollup: false,
              disabled: false,
              label: mirrorPrim.title,
              is_pk: false,
            })
          }
          if (newState.scopeLocks[rel.to_scope]) {
            newState.scopeLocks[rel.to_scope] = {
              ...newState.scopeLocks[rel.to_scope],
              relationColumnIndex: 0,
              unlockEdge: {
                primitiveId: relPrim.id,
                ownerScopeId: rel.from_scope,
                targetScopeId: rel.to_scope,
                mirrorPrimitiveId: mirrorPrim?.id ?? null,
              },
            }
          }
        }
      }

      // Mapping-title preset runs AFTER exportRelationChain so it can upgrade
      // an already-pushed relation row in place — otherwise we'd end up with
      // two rows (the structural unlock row + a new mapping-title row) for
      // the same primitive_id.  Upgrading means: flip is_mapping_title on
      // and override the label so the user sees the template's display name
      // (e.g. "Fremd-artikel-Nr." instead of the raw relation's own title).
      if (fullScopes) {
        for (const mf of mappingTitleFilters) {
          const ownerScope = fullScopes.find((s) => s.id === mf.scope_id)
          const relPrim = ownerScope?.primitives.find(
            (p) => p.name === mf.primitive_name && p.type === "relation",
          )
          if (!relPrim) continue
          const label = mf.label ?? relPrim.title
          const idx = newState.mappingRows.findIndex(
            (r) => r.primitive_id === relPrim.id,
          )
          if (idx >= 0) {
            newState.mappingRows[idx] = {
              ...newState.mappingRows[idx],
              is_mapping_title: true,
              label,
            }
          } else {
            newState.mappingRows.push({
              primitive_id: relPrim.id,
              primitive_scope_id: mf.scope_id,
              primitive_title: relPrim.title,
              primitive_type: "relation",
              column_index: 0,
              related_scope_id: relPrim.related_scope_id ?? null,
              is_mapping_title: true,
              typeCheck: null,
              cleaningInstruction: "",
              visible: true,
              isRollup: false,
              disabled: false,
              label,
              is_pk: false,
            })
          }
        }
      }

      dispatch({ type: "REPLACE_STATE", state: recalcLocks(newState) })
    } else {
      // Import mode: init when topology loads
      if (!topologyReady) return
      setInitialized(true)
      const newState = buildInitialState(
        scopeId,
        detected ?? emptyDetect,
        topology,
        pkPrimitiveId ?? "",
        pkPrimitiveTitle ?? "",
        pkPrimitiveType ?? "",
        false,
        headers,
        presetMappings,
        scopeNames,
      )
      dispatch({ type: "REPLACE_STATE", state: newState })
    }
  }, [
    initialized,
    isExportMode,
    topologyReady,
    exportLoading,
    exportAvailable,
    fullScopes,
  ])

  // Run type validation whenever rows change
  useEffect(() => {
    if (rows.length === 0) return
    dispatch({
      type: "MAP_COLUMN",
      primitiveId: "__type_recheck__",
      columnIndex: -999,
    })
    // The above is a no-op — instead we update type checks directly on the state
  }, [rows.length])

  // We need an imperative way to update type checks since they're derived
  const mappingRowsWithTypeChecks = useMemo(() => {
    if (rows.length === 0) return state.mappingRows
    return state.mappingRows.map((r) => {
      if (r.primitive_type === "relation") return { ...r, typeCheck: null }
      if (r.column_index < 0) return { ...r, typeCheck: null }
      const check = validateColumnType(
        rows,
        r.column_index,
        r.primitive_type as PrimitiveType,
      )
      return {
        ...r,
        typeCheck: {
          ...check,
          primitiveId: r.primitive_id,
          primitiveTitle: r.primitive_title,
          columnHeader: headers[r.column_index] ?? `Col ${r.column_index}`,
        },
      }
    })
  }, [state.mappingRows, rows.length, headers])

  // Derived state
  const usedColumnIndices = useMemo(() => {
    const claimed = new Set<number>()
    for (const m of state.mappingRows) {
      if (m.column_index >= 0) claimed.add(m.column_index)
      // Helpers targeting a doc header claim that column too — any regular
      // mapping's column dropdown then disables it, so a single doc column
      // can only be the export destination for one row (helper OR regular).
      if (m.isHelper && m.helperConfig?.targetDocHeader) {
        const idx = headers.indexOf(m.helperConfig.targetDocHeader)
        if (idx >= 0) claimed.add(idx)
      }
    }
    return claimed
  }, [state.mappingRows, headers])
  /** Doc headers already claimed by SOME row (regular mapping's column or
   *  helper's targetDocHeader).  The helper editor disables these so the
   *  user can't pick a doc header that's already an export destination.
   *  Scoped by caller: pass the current helper's own header (when editing)
   *  in `excluding` so it stays selectable. */
  const claimedDocHeaders = useMemo(() => {
    const set = new Set<string>()
    for (const m of state.mappingRows) {
      if (m.column_index >= 0 && m.column_index < headers.length) {
        const h = headers[m.column_index]
        if (h) set.add(h)
      }
      if (m.isHelper && m.helperConfig?.targetDocHeader) {
        set.add(m.helperConfig.targetDocHeader)
      }
    }
    return set
  }, [state.mappingRows, headers])
  const usedPrimitiveIds = useMemo(
    () => new Set(state.mappingRows.map((m) => m.primitive_id)),
    [state.mappingRows],
  )

  const unlockedScopeIds = useMemo(() => {
    const ids = new Set<string>()
    for (const [sid, lock] of Object.entries(state.scopeLocks)) {
      if (lock.unlocked) ids.add(sid)
    }
    return ids
  }, [state.scopeLocks])

  // Build tab list: fixed display_order from allScopesList (same order as main ScopeTabs).
  // Always show all scopes — locked ones are faded with a lock icon.
  const scopeTabIds = useMemo(() => {
    return allScopesList.map((s) => s.id)
  }, [allScopesList])

  const effectiveTab = scopeTabIds.includes(state.activeTab)
    ? state.activeTab
    : (scopeTabIds[0] ?? scopeId)
  const tabLock = state.scopeLocks[effectiveTab]
  const isTabLocked = effectiveTab !== scopeId && !tabLock?.unlocked
  const isTabPrimary = effectiveTab === scopeId

  // Anchor metadata derived from the scope config response (bi-directional:
  // both sides of a mapping-enabled relation can anchor via it).
  const primaryScopeDef = fullScopes?.find((s) => s.id === scopeId)
  const availableAnchorMappings: AnchorableMapping[] = useMemo(
    () => primaryScopeDef?.anchorable_mappings ?? [],
    [primaryScopeDef],
  )
  const activeMappingAnchor =
    state.anchorMode === "mapping" && state.anchorMappingPrimitiveId
      ? (availableAnchorMappings.find(
          (m) => m.primitive_id === state.anchorMappingPrimitiveId,
        ) ?? null)
      : null

  /** Ensure the chosen mapping primitive is represented in mappingRows so its
   *  column_index can be tracked.  The primitive may live on EITHER side of
   *  the relation (bi-directional) — we search across all scopes.  The target
   *  scope to auto-unlock comes from AnchorableMapping.related_scope_id, which
   *  is already "the other side from primary's perspective". */
  function handleToggleToMapping(mappingPrimitiveId: string) {
    const anchorable = availableAnchorMappings.find(
      (m) => m.primitive_id === mappingPrimitiveId,
    )
    if (!anchorable) return
    const existing = state.mappingRows.some(
      (r) => r.primitive_id === mappingPrimitiveId,
    )
    if (!existing && fullScopes) {
      for (const s of fullScopes) {
        const prim = s.primitives.find((p) => p.id === mappingPrimitiveId)
        if (prim) {
          dispatch({
            type: "ADD_PRIMITIVE",
            primitive: prim,
            detected: state.detectResults[s.id] ?? null,
          })
          break
        }
      }
    }
    dispatch({
      type: "SET_ANCHOR_MODE_MAPPING",
      mappingPrimitiveId,
      targetScopeId: anchorable.related_scope_id,
    })
  }

  // Group rows by scope for current tab
  const tabMappings = mappingRowsWithTypeChecks.filter(
    (r) => r.primitive_scope_id === effectiveTab,
  )

  // Fetch full scope details for active tab — used for "Add column"
  const { data: tabScope } = useQuery({
    queryKey: ["scope", effectiveTab],
    queryFn: () => api.getScope(effectiveTab),
    enabled: !!effectiveTab,
  })

  // Available primitives for the active tab: not already mapped, not formula, not hidden
  const tabAvailablePrims = useMemo(() => {
    const prims = tabScope?.primitives ?? []
    return prims.filter((p) => {
      if (usedPrimitiveIds.has(p.id)) return false
      if (p.type === "formula") return false
      if (p.config.hidden) return false
      // In secondary scopes, exclude PK (relation anchor IS the PK)
      if (effectiveTab !== scopeId && p.is_pk) return false
      return true
    })
  }, [tabScope, usedPrimitiveIds, effectiveTab, scopeId])

  const [addDropdownOpen, setAddDropdownOpen] = useState(false)
  const [addSearch, setAddSearch] = useState("")
  const tabFilteredAvailable = addSearch
    ? tabAvailablePrims.filter(
        (p) =>
          p.title.toLowerCase().includes(addSearch.toLowerCase()) ||
          p.name.toLowerCase().includes(addSearch.toLowerCase()),
      )
    : tabAvailablePrims

  // Rollup picker state
  const [rollupDropdownOpen, setRollupDropdownOpen] = useState(false)
  const [rollupSearch, setRollupSearch] = useState("")

  // Helper editor state — open either to add a new helper on the active tab
  // ({ mode: 'add' }) or to edit an existing one ({ mode: 'edit', primitive_id }).
  const [helperEditor, setHelperEditor] = useState<
    { mode: "add" } | { mode: "edit"; primitiveId: string } | null
  >(null)

  // Rollup-eligible primitives: non-relation, non-formula fields from OTHER unlocked scopes
  const rollupAvailablePrims = useMemo(() => {
    const result: {
      scopeId: string
      scopeName: string
      primitives: PrimitiveDef[]
    }[] = []
    const existingRollupIds = new Set(
      state.mappingRows
        .filter((r) => r.rollupConfig)
        .map((r) => r.rollupConfig!.sourcePrimitiveId),
    )
    for (const sid of unlockedScopeIds) {
      if (sid === effectiveTab) continue
      const scopeDef = fullScopes?.find((s) => s.id === sid)
      if (!scopeDef) continue
      const prims = scopeDef.primitives.filter(
        (p) =>
          p.type !== "relation" &&
          p.type !== "formula" &&
          !p.config.hidden &&
          !existingRollupIds.has(p.id),
      )
      if (prims.length > 0) {
        result.push({
          scopeId: sid,
          scopeName: scopeNames[sid] ?? sid,
          primitives: prims,
        })
      }
    }
    return result
  }, [
    unlockedScopeIds,
    effectiveTab,
    fullScopes,
    scopeNames,
    state.mappingRows,
  ])

  const rollupHasOtherUnlocked = unlockedScopeIds.size > 1

  const rollupFilteredPrims = useMemo(() => {
    if (!rollupSearch) return rollupAvailablePrims
    const q = rollupSearch.toLowerCase()
    return rollupAvailablePrims
      .map((group) => ({
        ...group,
        primitives: group.primitives.filter(
          (p) =>
            p.title.toLowerCase().includes(q) ||
            p.name.toLowerCase().includes(q),
        ),
      }))
      .filter((group) => group.primitives.length > 0)
  }, [rollupAvailablePrims, rollupSearch])

  // Lazy-load detect results for newly visible scopes
  useEffect(() => {
    for (const sid of scopeTabIds) {
      if (sid === scopeId) continue // Primary already has detect
      if (state.detectResults[sid]) continue // Already loaded
      // Fire detect for this scope
      if (headers.length > 0) {
        api
          .ingestDetect(sid, {
            file_type: "excel",
            headers,
            sample_rows: rows.slice(0, 5),
          })
          .then((result) =>
            dispatch({
              type: "ADD_SCOPE_DETECT",
              scopeId: sid,
              detected: result,
            }),
          )
          .catch(() => {
            /* ignore — scope just won't have fuzzy suggestions */
          })
      }
    }
  }, [scopeTabIds, headers, state.detectResults])

  // Close dropdowns when switching tabs
  useEffect(() => {
    setAddDropdownOpen(false)
    setAddSearch("")
    setRollupDropdownOpen(false)
    setRollupSearch("")
  }, [effectiveTab])

  // Find the relation anchor for a secondary scope tab
  const getRelationAnchor = (tabScopeId: string): MappingRow | undefined => {
    // Look for a relation row (in any scope) that connects to this tab scope
    return state.mappingRows.find(
      (r) =>
        r.primitive_type === "relation" &&
        r.related_scope_id === tabScopeId &&
        r.column_index >= 0,
    )
  }

  // Find which relation primitives can unlock a locked scope (for the guidance dropdown)
  const getUnlockRelations = (
    tabScopeId: string,
  ): { primitive: PrimitiveDef; fromScopeId: string }[] => {
    const result: { primitive: PrimitiveDef; fromScopeId: string }[] = []
    // Track which scope pairs we've already covered to avoid showing both sides
    // of the same bidirectional relation
    const coveredPairs = new Set<string>()

    // Prefer relations from unlocked scopes pointing to this locked scope
    for (const sid of unlockedScopeIds) {
      const edges = topology.edges.get(sid) ?? []
      for (const edge of edges) {
        if (edge.targetScopeId !== tabScopeId) continue
        if (usedPrimitiveIds.has(edge.primitiveId)) continue
        const pairKey = [sid, tabScopeId].sort().join("|")
        if (coveredPairs.has(pairKey)) continue
        coveredPairs.add(pairKey)
        const scopeDef = fullScopes?.find((s) => s.id === sid)
        const prim = scopeDef?.primitives.find((p) => p.id === edge.primitiveId)
        if (prim) result.push({ primitive: prim, fromScopeId: sid })
      }
    }
    // Fallback: relations from THIS scope pointing to unlocked scopes (only if not already covered)
    const edges = topology.edges.get(tabScopeId) ?? []
    for (const edge of edges) {
      if (!unlockedScopeIds.has(edge.targetScopeId)) continue
      if (usedPrimitiveIds.has(edge.primitiveId)) continue
      const pairKey = [tabScopeId, edge.targetScopeId].sort().join("|")
      if (coveredPairs.has(pairKey)) continue
      coveredPairs.add(pairKey)
      const scopeDef = fullScopes?.find((s) => s.id === tabScopeId)
      const prim = scopeDef?.primitives.find((p) => p.id === edge.primitiveId)
      if (prim) result.push({ primitive: prim, fromScopeId: tabScopeId })
    }
    return result
  }

  // Validation
  const hasUnresolvedFlags = mappingRowsWithTypeChecks.some(
    (r) =>
      r.visible &&
      !r.disabled &&
      r.typeCheck &&
      !r.typeCheck.compatible &&
      r.cleaningInstruction.trim() === "",
  )
  const hasAnyRollup = state.mappingRows.some((m) => m.isRollup)
  /** Anchor rows (PK / mapping anchor) are rendered in the AnchorBox, not
   *  the column table — they shouldn't inflate the "n Felder" counts either. */
  const isAnchorRow = (r: MappingRow): boolean => {
    if (r.primitive_scope_id !== scopeId) return false
    if (r.primitive_id === pkPrimitiveId) return true
    if (
      state.anchorMode === "mapping" &&
      r.primitive_id === state.anchorMappingPrimitiveId
    )
      return true
    return false
  }
  const unlockedVisibleCount = mappingRowsWithTypeChecks.filter(
    (m) =>
      m.visible &&
      !m.disabled &&
      !isAnchorRow(m) &&
      (state.scopeLocks[m.primitive_scope_id]?.unlocked ??
        m.primitive_scope_id === scopeId),
  ).length

  // ---------------------------------------------------------------------------
  // Proceed
  // ---------------------------------------------------------------------------

  function proceed() {
    // Collect mappings from all unlocked scopes
    const mappings: UserMapping[] = mappingRowsWithTypeChecks
      .filter((r) => r.visible && !r.disabled)
      .filter((r) => {
        const lock = state.scopeLocks[r.primitive_scope_id]
        return lock?.unlocked ?? r.primitive_scope_id === scopeId
      })
      .map((r) => ({
        primitive_id: r.primitive_id,
        primitive_scope_id: r.primitive_scope_id,
        primitive_title: r.primitive_title,
        primitive_type: r.primitive_type,
        column_index: r.column_index,
        related_scope_id: r.related_scope_id ?? null,
        is_mapping_title: r.is_mapping_title ?? false,
        isRollup: r.isRollup ?? false,
        isHelper: r.isHelper ?? false,
        helperConfig: r.helperConfig,
      }))

    // Write scope mappings to actionState for secondary scopes
    const store = useStore.getState()
    if (store.actionState) {
      const newScopeMappings = { ...store.actionState.scopeMappings }
      for (const [sid, lock] of Object.entries(state.scopeLocks)) {
        if (sid === scopeId) continue
        if (!lock.unlocked) continue
        // Find the relation primitive that unlocks this scope
        const relationRow = state.mappingRows.find(
          (r) =>
            r.primitive_type === "relation" &&
            r.related_scope_id === sid &&
            r.column_index >= 0,
        )
        if (relationRow) {
          newScopeMappings[sid] = {
            status: "user-edited",
            relationPrimitiveId: relationRow.primitive_id,
            documentColumn: relationRow.column_index,
            additionalMappings: [],
          }
        }
      }
      useStore.setState({
        actionState: { ...store.actionState, scopeMappings: newScopeMappings },
      })
    }

    if (isExportMode && onExportProceed) {
      const columns: DownloadColumn[] = state.mappingRows
        .filter((r) => r.visible && !r.disabled)
        .filter((r) => r.primitive_type !== "relation") // relations are structural, not data columns
        .filter((r) => {
          const lock = state.scopeLocks[r.primitive_scope_id]
          return lock?.unlocked ?? r.primitive_scope_id === scopeId
        })
        .map((r) => ({
          primitive_id: r.primitive_id,
          scope_id: r.primitive_scope_id,
          label: r.label ?? r.primitive_title,
          rollup: !!r.rollupConfig,
          rollupConfig: r.rollupConfig,
          // Preserve helperConfig so the diff view (and later consumers)
          // can render and evaluate the formula per record.  Helpers are
          // virtual — they don't correspond to real DB primitives, so any
          // code that treats them like backed primitives must key off this
          // field.
          helperConfig: r.helperConfig
            ? {
                label: r.helperConfig.label,
                expression: r.helperConfig.expression,
              }
            : undefined,
        }))
      onExportProceed(columns, exportFormatState ?? "csv")
      return
    }
    // Anchor choice travels alongside the mappings so the extraction step can
    // hit /ingest/anchor with the correct mode.  Mapping-mode column index is
    // resolved here (from the in-flight MappingRow) so callers don't need to
    // reach back into reducer state.
    const anchorChoice: AnchorChoice =
      state.anchorMode === "mapping"
        ? {
            mode: "mapping",
            mappingPrimitiveId: state.anchorMappingPrimitiveId,
            mappingColumnIndex: state.anchorMappingPrimitiveId
              ? (state.mappingRows.find(
                  (r) => r.primitive_id === state.anchorMappingPrimitiveId,
                )?.column_index ?? -1)
              : -1,
          }
        : { mode: "pk", mappingPrimitiveId: null, mappingColumnIndex: -1 }
    onProceed?.(mappings, anchorChoice)
  }

  // ---------------------------------------------------------------------------
  // Render — single-record mode (simple, no cross-scope)
  // ---------------------------------------------------------------------------

  if (pkOptional) {
    return (
      <SimpleMappingView
        scopeId={scopeId}
        headers={headers}
        rows={rows}
        previewRows={previewRows}
        mappingRows={mappingRowsWithTypeChecks.filter(
          (r) => r.primitive_scope_id === scopeId,
        )}
        pkPrimitiveId={pkPrimitiveId ?? null}
        pkPrimitiveTitle={pkPrimitiveTitle ?? ""}
        pkValue={pkValue}
        usedColumnIndices={usedColumnIndices}
        tabAvailablePrims={tabAvailablePrims}
        detected={detected ?? emptyDetect}
        hasUnresolvedFlags={hasUnresolvedFlags}
        dispatch={dispatch}
        onProceed={proceed}
      />
    )
  }

  // ---------------------------------------------------------------------------
  // Render — multi-record cross-scope mode
  // ---------------------------------------------------------------------------

  return (
    <div>
      <p className="mb-4 text-sm text-grey5">
        {translate("QMS/COLUMN_MAPPING/MAP_COLUMNS_DESC")}{" "}
        {translate("QMS/INGESTION/FIELDS_SHOWN")}
      </p>

      {/* --- Scope tabs --- */}
      {allScopesLoading ? (
        <div className="mb-0 flex items-center gap-2 border-b border-grey4 py-3">
          <div className="h-3 w-3 animate-spin rounded-full border-2 border-grey4 border-t-yellow1" />
          <span className="text-xs text-grey5">
            {translate("QMS/INGESTION/SCOPES_LOADING")}
          </span>
        </div>
      ) : (
        <div className="mb-0 flex items-end gap-0.5 overflow-x-auto border-b border-grey4">
          {scopeTabIds.map((sid) => {
            const scopeMappings = mappingRowsWithTypeChecks.filter((r) => {
              if (r.primitive_scope_id !== sid || r.disabled) return false
              // Anchor rows are shown in the AnchorBox, not the column table —
              // keep the tab count aligned with what the user sees below.
              if (sid === scopeId && r.primitive_id === pkPrimitiveId)
                return false
              if (
                sid === scopeId &&
                state.anchorMode === "mapping" &&
                r.primitive_id === state.anchorMappingPrimitiveId
              )
                return false
              return true
            })
            const scopeVisible = scopeMappings.filter((m) => m.visible).length
            const scopeLabel = scopeNames[sid] ?? sid
            const isActive = sid === effectiveTab
            const lock = state.scopeLocks[sid]
            const locked = sid !== scopeId && !lock?.unlocked

            return (
              <button
                key={sid}
                onClick={() =>
                  dispatch({ type: "SET_ACTIVE_TAB", scopeId: sid })
                }
                className={clsx(
                  "flex items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-2 text-xs font-medium transition-colors",
                  isActive
                    ? locked
                      ? "border-grey5 text-grey5"
                      : "border-yellow1 text-yellow1"
                    : "border-transparent text-grey5 hover:border-grey4 hover:text-white",
                  locked && !isActive && "opacity-60",
                )}
              >
                {locked && (
                  <svg
                    className="h-3 w-3 text-grey5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                )}
                <span>{scopeLabel}</span>
                {!locked && scopeMappings.length > 0 && (
                  <span
                    className={clsx(
                      "text-[10px] tabular-nums",
                      isActive ? "text-yellow1" : "text-grey5",
                    )}
                  >
                    {scopeVisible}/{scopeMappings.length}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}

      {/* --- Locked scope guidance --- */}
      {isTabLocked && (
        <div className="my-3 rounded-lg border border-grey4 bg-grey1 p-4">
          <div className="mb-2 flex items-center gap-2">
            <svg
              className="h-4 w-4 text-warning"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <span className="text-sm font-medium text-warning">
              {translate("QMS/COLUMN_MAPPING/SCOPE_LOCKED")}
            </span>
          </div>
          <p className="mb-3 text-xs text-grey5">
            {getUnlockGuidance(
              topology,
              scopeId,
              effectiveTab,
              unlockedScopeIds,
              scopeNames,
              translate,
            )}
          </p>

          {/* Unlock relation dropdown — user can unlock from this tab */}
          {(() => {
            const unlockOptions = getUnlockRelations(effectiveTab)
            if (unlockOptions.length === 0) return null
            return (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-grey5">
                  {translate("QMS/INGESTION/RELATION_MAPPING")}:
                </span>
                {unlockOptions.map((opt) => (
                  <div
                    key={opt.primitive.id}
                    className="flex items-center gap-1.5"
                  >
                    <span className="text-xs font-medium text-purple1">
                      {opt.primitive.title}
                      <span className="ml-1 font-normal text-grey5">
                        ({scopeNames[opt.fromScopeId] ?? opt.fromScopeId})
                      </span>
                    </span>
                    {isExportMode ? (
                      <button
                        onClick={() => {
                          dispatch({
                            type: "ADD_PRIMITIVE",
                            primitive: opt.primitive,
                            detected:
                              state.detectResults[opt.fromScopeId] ?? null,
                          })
                        }}
                        className="rounded border border-yellow5 bg-grey2 px-2 py-1 text-xs text-yellow1 transition-colors hover:bg-yellow5/10"
                      >
                        {translate("QMS/COLUMN_MAPPING/UNLOCK_BTN")}
                      </button>
                    ) : (
                      <select
                        value={-1}
                        onChange={(e) => {
                          const colIdx = Number(e.target.value)
                          if (colIdx < 0) return
                          // Add the relation primitive and map it
                          dispatch({
                            type: "ADD_PRIMITIVE",
                            primitive: opt.primitive,
                            detected:
                              state.detectResults[opt.fromScopeId] ?? null,
                          })
                          dispatch({
                            type: "MAP_RELATION",
                            primitiveId: opt.primitive.id,
                            columnIndex: colIdx,
                            targetScopeId: effectiveTab,
                          })
                        }}
                        className="rounded border border-grey4 bg-grey2 px-1.5 py-1 text-xs text-white"
                      >
                        <option value={-1}>
                          {translate("QMS/INGESTION/SELECT_COL")}
                        </option>
                        {headers.map((h, idx) => (
                          <option
                            key={idx}
                            value={idx}
                            disabled={usedColumnIndices.has(idx)}
                          >
                            {h ||
                              translate("QMS/COLUMN_MAPPING/COLUMN_FALLBACK", {
                                index: idx + 1,
                              })}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            )
          })()}

          {/* Show preserved (disabled) mappings */}
          {tabMappings.filter((r) => r.disabled).length > 0 && (
            <div className="mt-3 border-t border-grey4 pt-3">
              <p className="mb-2 text-[10px] uppercase tracking-wide text-grey5">
                {translate("QMS/COLUMN_MAPPING/SAVED_MAPPINGS_LOCKED")}
              </p>
              {tabMappings
                .filter((r) => r.disabled)
                .map((r) => (
                  <div
                    key={r.primitive_id}
                    className="flex items-center gap-2 py-1 opacity-40"
                  >
                    <span className="font-mono text-xs text-grey5">
                      [{r.primitive_type}]
                    </span>
                    <span className="text-xs text-grey5">
                      {r.primitive_title}
                    </span>
                    {r.column_index >= 0 && (
                      <span className="font-mono text-xs text-grey5">
                        →{" "}
                        {headers[r.column_index] ??
                          translate("QMS/COLUMN_MAPPING/COLUMN_FALLBACK", {
                            index: r.column_index,
                          })}
                      </span>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* --- Primary scope anchor box: PK / Mapping toggle --- */}
      {!isTabLocked && isTabPrimary && pkPrimitiveId && (
        <AnchorBox
          role="primary"
          scopeName={scopeNames[scopeId] ?? scopeId}
          headers={headers}
          usedColumnIndices={usedColumnIndices}
          isExportMode={isExportMode}
          scopeNames={scopeNames}
          primary={{
            pkPrimitiveTitle: pkPrimitiveTitle ?? "",
            pkColumnIndex: state.pkColumnIndex,
            availableMappings: availableAnchorMappings,
            mode: state.anchorMode,
            mappingPrimitiveId: state.anchorMappingPrimitiveId,
            mappingColumnIndex: activeMappingAnchor
              ? (state.mappingRows.find(
                  (r) => r.primitive_id === activeMappingAnchor.primitive_id,
                )?.column_index ?? -1)
              : -1,
            onPkColumnChange: (colIdx) =>
              dispatch({ type: "MAP_PK", columnIndex: colIdx }),
            onMappingColumnChange: (colIdx) =>
              dispatch({
                type: "SET_ANCHOR_MAPPING_COLUMN",
                columnIndex: colIdx,
              }),
            onModeChange: (mode, mappingPrimitiveId) => {
              if (mode === "pk") dispatch({ type: "SET_ANCHOR_MODE_PK" })
              else if (mappingPrimitiveId)
                handleToggleToMapping(mappingPrimitiveId)
            },
          }}
        />
      )}

      {/* --- Unlocked secondary scope: relation anchor ---
           Two unlock paths co-exist:
           (a) A regular relation column mapped from the parent scope — the
               mirror primitive's row on this tab is the anchor.
           (b) The primary scope is in mapping mode AND THIS is the auto-
               unlocked partner scope; the anchor is the primary's chosen
               relation primitive, surfaced here read-only. */}
      {!isTabLocked &&
        !isTabPrimary &&
        (() => {
          const isMappingAnchorTarget =
            state.anchorMode === "mapping" &&
            state.anchorMappingTargetScopeId === effectiveTab &&
            state.anchorMappingPrimitiveId != null
          const anchor = isMappingAnchorTarget
            ? (state.mappingRows.find(
                (r) => r.primitive_id === state.anchorMappingPrimitiveId,
              ) ?? null)
            : (getRelationAnchor(effectiveTab) ?? null)
          if (!anchor) return null
          const parentScopeName = isMappingAnchorTarget
            ? (scopeNames[scopeId] ?? scopeId)
            : (scopeNames[anchor.primitive_scope_id] ??
              anchor.primitive_scope_id)
          const sharedWithPrimary = isMappingAnchorTarget
          return (
            <AnchorBox
              role="secondary"
              scopeName={scopeNames[effectiveTab] ?? effectiveTab}
              headers={headers}
              usedColumnIndices={usedColumnIndices}
              isExportMode={isExportMode}
              secondary={{
                parentScopeName,
                anchorPrimitiveTitle: anchor.primitive_title,
                columnIndex: anchor.column_index,
                sharedWithPrimary,
                onColumnChange: (colIdx) => {
                  const targetScope = isMappingAnchorTarget
                    ? effectiveTab
                    : effectiveTab
                  if (colIdx < 0) {
                    dispatch({
                      type: "UNMAP_RELATION",
                      primitiveId: anchor.primitive_id,
                      targetScopeId: targetScope,
                    })
                  } else {
                    dispatch({
                      type: "MAP_RELATION",
                      primitiveId: anchor.primitive_id,
                      columnIndex: colIdx,
                      targetScopeId: targetScope,
                    })
                  }
                },
              }}
            />
          )
        })()}

      {/* --- Tab toolbar (only for unlocked tabs, hidden in readOnly) --- */}
      {!isTabLocked && !readOnly && (
        <div className="flex items-center justify-between py-2">
          <div className="flex gap-2">
            <button
              onClick={() =>
                dispatch({
                  type: "TOGGLE_ALL_VISIBLE",
                  scopeId: effectiveTab,
                  visible: true,
                })
              }
              className="text-[10px] text-yellow1 hover:underline"
            >
              {translate("QMS/COLUMN_MAPPING/SHOW_ALL")}
            </button>
            <button
              onClick={() =>
                dispatch({
                  type: "TOGGLE_ALL_VISIBLE",
                  scopeId: effectiveTab,
                  visible: false,
                })
              }
              className="text-[10px] text-yellow1 hover:underline"
            >
              {translate("QMS/COLUMN_MAPPING/HIDE_ALL")}
            </button>
          </div>
          <div className="flex items-center gap-2">
            {/* Rollup / Rolldown button — enabled when the flow is export-
                bound (PivotTable export OR upload-based export wizard).
                Import flows show the Rolldown stub, disabled until built. */}
            {(() => {
              const rollupAvailable = isExportMode || !!isExportFlow
              const rollupEnabled = rollupAvailable && rollupHasOtherUnlocked
              return (
                <div className="relative">
                  <button
                    onClick={() => {
                      if (rollupEnabled) {
                        setRollupDropdownOpen((v) => !v)
                        setAddDropdownOpen(false)
                      }
                    }}
                    disabled={!rollupEnabled}
                    className={clsx(
                      "flex items-center gap-1 rounded-lg border px-2.5 py-1 text-[11px] font-medium transition-colors",
                      !rollupEnabled
                        ? "cursor-not-allowed border-grey4 text-grey5 opacity-50"
                        : "border-purple1/50 text-purple1 hover:bg-purple1/10",
                    )}
                    title={
                      !rollupAvailable
                        ? translate("QMS/COLUMN_MAPPING/ROLLDOWN_COMING_SOON")
                        : !rollupHasOtherUnlocked
                          ? translate("QMS/COLUMN_MAPPING/UNLOCK_MORE_SCOPES")
                          : undefined
                    }
                  >
                    <svg
                      className="h-3 w-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    {translate("QMS/INGESTION/ADD")}
                  </button>
                  {rollupDropdownOpen && (
                    <div className="absolute right-0 top-full z-10 mt-1 w-80 overflow-hidden rounded-lg border border-purple1/30 bg-grey2 shadow-lg">
                      <div className="border-b border-grey4 p-2">
                        <input
                          type="text"
                          value={rollupSearch}
                          onChange={(e) => setRollupSearch(e.target.value)}
                          placeholder={translate(
                            "QMS/INGESTION/SEARCH_FIELDS_PLACEHOLDER",
                          )}
                          className="w-full rounded-md border border-grey4 px-2.5 py-1.5 text-xs focus:outline-hidden focus:ring-1 focus:ring-purple1"
                          autoFocus
                        />
                      </div>
                      <div className="max-h-60 overflow-y-auto">
                        {rollupFilteredPrims.length === 0 ? (
                          <p className="px-3 py-2 text-xs text-grey5">
                            {rollupAvailablePrims.length === 0
                              ? translate("QMS/INGESTION/ALL_COLUMNS_ADDED")
                              : translate("QMS/COMMON/NO_RESULTS")}
                          </p>
                        ) : (
                          rollupFilteredPrims.map((group) => (
                            <div key={group.scopeId}>
                              <div className="sticky top-0 border-b border-grey4 bg-grey1 px-3 py-1.5">
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-purple1">
                                  {group.scopeName}
                                </span>
                              </div>
                              {group.primitives.map((p) => (
                                <button
                                  key={p.id}
                                  onClick={() => {
                                    const agg: RollupAggregation =
                                      p.type === "number"
                                        ? "sum"
                                        : "concat_unique"
                                    dispatch({
                                      type: "ADD_ROLLUP",
                                      targetScopeId: effectiveTab,
                                      sourcePrimitive: p,
                                      aggregation: agg,
                                    })
                                    setRollupDropdownOpen(false)
                                    setRollupSearch("")
                                  }}
                                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors hover:bg-purple1/10"
                                >
                                  <span className="font-medium text-white">
                                    {p.title}
                                  </span>
                                  <span className="font-mono text-[10px] text-grey5">
                                    [{p.type}]
                                  </span>
                                  {p.is_pk && (
                                    <span className="rounded bg-yellow5/20 px-1 py-0.5 text-[9px] font-medium text-yellow1">
                                      PK
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })()}

            {/* translate('QMS/INGESTION/ADD') — user-authored formula column, virtual, per tab */}
            <div className="relative">
              <button
                onClick={() => {
                  setHelperEditor({ mode: "add" })
                  setAddDropdownOpen(false)
                  setRollupDropdownOpen(false)
                }}
                className="flex items-center gap-1 rounded-lg border border-success/50 px-2.5 py-1 text-[11px] font-medium text-success transition-colors hover:bg-success/10"
              >
                <svg
                  className="h-3 w-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                {translate("QMS/INGESTION/ADD")}
              </button>
              {helperEditor &&
                (() => {
                  const editingRow =
                    helperEditor.mode === "edit"
                      ? state.mappingRows.find(
                          (r) => r.primitive_id === helperEditor.primitiveId,
                        )
                      : null
                  const refs: HelperRef[] = []
                  // Primitive refs: same-tab non-relation primitives that are mapped OR already present.
                  // We expose by `name` so the identifier matches backend formula semantics.
                  const tabScopeDef = fullScopes?.find(
                    (s) => s.id === effectiveTab,
                  )
                  if (tabScopeDef) {
                    for (const p of tabScopeDef.primitives) {
                      if (p.type === "relation" || p.type === "formula")
                        continue
                      if (!p.name) continue
                      refs.push({
                        identifier: p.name,
                        title: p.title,
                        group: "Primitive",
                      })
                    }
                  }
                  // Rollup refs: other rollups on this tab.
                  for (const r of state.mappingRows) {
                    if (r.primitive_scope_id !== effectiveTab) continue
                    if (r.rollupConfig && r.label) {
                      const slug = r.label.replace(/[^\w]/g, "_")
                      refs.push({
                        identifier: slug,
                        title: `${translate("QMS/COLUMN_MAPPING/ROLLUP_BADGE")}: ${r.label}`,
                        group: translate("QMS/COLUMN_MAPPING/ROLLUP_BADGE"),
                      })
                    }
                  }
                  // Helper refs: other helpers on this tab (not self when editing).
                  const existingLabels: string[] = []
                  for (const r of state.mappingRows) {
                    if (r.primitive_scope_id !== effectiveTab) continue
                    if (!r.isHelper || !r.helperConfig) continue
                    if (
                      editingRow &&
                      r.primitive_id === editingRow.primitive_id
                    )
                      continue
                    existingLabels.push(r.helperConfig.label)
                    refs.push({
                      identifier: r.helperConfig.label,
                      title: `${translate("QMS/HELPER/GROUP_LABEL")}: ${r.helperConfig.label}`,
                      group: translate("QMS/HELPER/GROUP_LABEL"),
                    })
                  }
                  // Doc headers to disable in the target picker: everything
                  // already claimed by another row, except this helper's own
                  // current target (so editing stays idempotent).
                  const ownTarget =
                    editingRow?.helperConfig?.targetDocHeader ?? null
                  const disabledDocHeaders = Array.from(
                    claimedDocHeaders,
                  ).filter((h) => h !== ownTarget)
                  return (
                    <HelperEditor
                      initial={
                        editingRow?.helperConfig
                          ? {
                              label: editingRow.helperConfig.label,
                              expression: editingRow.helperConfig.expression,
                              targetDocHeader:
                                editingRow.helperConfig.targetDocHeader ?? null,
                            }
                          : undefined
                      }
                      existingLabels={existingLabels}
                      availableRefs={refs}
                      docHeaders={headers.filter((h) => !!h)}
                      disabledDocHeaders={disabledDocHeaders}
                      onSave={(label, expression, targetDocHeader) => {
                        if (editingRow) {
                          dispatch({
                            type: "UPDATE_HELPER",
                            primitiveId: editingRow.primitive_id,
                            label,
                            expression,
                            targetDocHeader,
                          })
                        } else {
                          dispatch({
                            type: "ADD_HELPER",
                            scopeId: effectiveTab,
                            label,
                            expression,
                            targetDocHeader,
                          })
                        }
                        setHelperEditor(null)
                      }}
                      onCancel={() => setHelperEditor(null)}
                    />
                  )
                })()}
            </div>

            {/* Add column */}
            <div className="relative">
              <button
                onClick={() => {
                  setAddDropdownOpen((v) => !v)
                  setRollupDropdownOpen(false)
                }}
                className="flex items-center gap-1 rounded-lg border border-yellow5 px-2.5 py-1 text-[11px] font-medium text-yellow1 transition-colors hover:bg-yellow5/10"
              >
                <svg
                  className="h-3 w-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                {translate("QMS/INGESTION/ADD_COLUMN")}
              </button>
              {addDropdownOpen && (
                <div className="absolute right-0 top-full z-10 mt-1 w-72 overflow-hidden rounded-lg border border-grey4 bg-grey2 shadow-lg">
                  <div className="border-b p-2">
                    <input
                      type="text"
                      value={addSearch}
                      onChange={(e) => setAddSearch(e.target.value)}
                      placeholder={translate(
                        "QMS/INGESTION/SEARCH_FIELDS_PLACEHOLDER",
                      )}
                      className="w-full rounded-md border border-grey4 px-2.5 py-1.5 text-xs focus:outline-hidden focus:ring-1 focus:ring-yellow1"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {tabFilteredAvailable.length === 0 ? (
                      <p className="px-3 py-2 text-xs text-grey5">
                        {tabAvailablePrims.length === 0
                          ? translate("QMS/INGESTION/ALL_COLUMNS_ADDED")
                          : translate("QMS/COMMON/NO_RESULTS")}
                      </p>
                    ) : (
                      tabFilteredAvailable.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => {
                            dispatch({
                              type: "ADD_PRIMITIVE",
                              primitive: p,
                              detected:
                                state.detectResults[effectiveTab] ?? null,
                            })
                            setAddDropdownOpen(false)
                            setAddSearch("")
                          }}
                          className="flex w-full items-center justify-between px-3 py-2 text-left text-xs transition-colors hover:bg-grey1"
                        >
                          <span>
                            <span className="font-medium text-white">
                              {p.title}
                            </span>
                            <span className="font-mono ml-1.5 text-[10px] text-grey5">
                              [{p.type}]
                            </span>
                            {p.is_pk && (
                              <span className="ml-1 rounded bg-yellow5/20 px-1 py-0.5 text-[9px] font-medium text-yellow1">
                                PK
                              </span>
                            )}
                            {p.type === "relation" && p.related_scope_id && (
                              <span className="ml-1 text-[10px] text-purple1">
                                →{" "}
                                {scopeNames[p.related_scope_id] ??
                                  p.related_scope_id}
                              </span>
                            )}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- Column table for active tab (unlocked only) --- */}
      {!isTabLocked && (
        <MappingTable
          rows={(() => {
            let filtered = isTabPrimary
              ? tabMappings
              : tabMappings.filter((r) => !r.disabled)
            // Primary tab: hide the PK row (promoted to AnchorBox) and, when
            // the primary anchor is a mapping, hide that relation's row too
            // (its column is already claimed by the AnchorBox).
            if (isTabPrimary) {
              filtered = filtered.filter(
                (r) => r.primitive_id !== pkPrimitiveId,
              )
              if (
                state.anchorMode === "mapping" &&
                state.anchorMappingPrimitiveId
              ) {
                filtered = filtered.filter(
                  (r) => r.primitive_id !== state.anchorMappingPrimitiveId,
                )
              }
            }
            // In secondary tabs, hide the mirror relation row shown in the anchor bar
            if (!isTabPrimary) {
              const anchor = getRelationAnchor(effectiveTab)
              if (anchor) {
                // The anchor is on the parent scope; the mirror is the relation in THIS tab
                // pointing back to the parent scope
                filtered = filtered.filter((r) => {
                  if (r.primitive_type !== "relation") return true
                  // Hide if this relation points to the scope that owns the anchor
                  return r.related_scope_id !== anchor.primitive_scope_id
                })
              }
              // Mapping-anchor case: the anchor primitive lives on this tab's
              // scope but is surfaced above via the shared AnchorBox.  Hide
              // its in-table row so the column isn't exposed twice.
              if (
                state.anchorMode === "mapping" &&
                state.anchorMappingTargetScopeId === effectiveTab &&
                state.anchorMappingPrimitiveId
              ) {
                filtered = filtered.filter(
                  (r) => r.primitive_id !== state.anchorMappingPrimitiveId,
                )
              }
            }
            return filtered
          })()}
          headers={headers}
          scopeId={scopeId}
          effectiveTab={effectiveTab}
          pkPrimitiveId={pkPrimitiveId ?? null}
          usedColumnIndices={usedColumnIndices}
          scopeNames={scopeNames}
          dispatch={dispatch}
          mode={mode}
          readOnly={readOnly}
          onEditHelper={(primitiveId) =>
            setHelperEditor({ mode: "edit", primitiveId })
          }
        />
      )}

      {/* Preview — import: CSV vs DB; export: DB values for selected records */}
      {!isExportMode &&
        !isTabLocked &&
        tabMappings.filter((r) => r.visible && !r.disabled).length > 0 &&
        previewRows.length > 0 && (
          <DiffPreview
            previewRows={previewRows}
            mappingRows={mappingRowsWithTypeChecks}
            headers={headers}
            scopeId={scopeId}
            pkPrimitiveId={pkPrimitiveId ?? null}
            anchorMode={state.anchorMode}
            anchorMappingPrimitiveId={state.anchorMappingPrimitiveId}
            anchorMappingColumnIndex={
              activeMappingAnchor
                ? (state.mappingRows.find(
                    (r) => r.primitive_id === activeMappingAnchor.primitive_id,
                  )?.column_index ?? -1)
                : -1
            }
            anchorMappingTitle={activeMappingAnchor?.mapping_title ?? ""}
            allRows={rows}
            allHeaders={headers}
          />
        )}
      {isExportMode &&
        !isTabLocked &&
        tabMappings.filter((r) => r.visible && !r.disabled).length > 0 && (
          <ExportPreview
            scopeId={scopeId}
            mappingRows={mappingRowsWithTypeChecks.filter(
              (r) =>
                r.visible && !r.disabled && r.primitive_scope_id === scopeId,
            )}
            selectedRecordIds={selectedRecordIds}
          />
        )}

      {!isExportMode && hasUnresolvedFlags && (
        <div className="mb-3 rounded-lg border border-warning bg-warning/10 p-2.5 text-xs text-warning">
          {translate("QMS/INGESTION/FIELDS_SHOWN")}
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-xs text-grey5">
          {isExportMode
            ? translate("QMS/COLUMN_MAPPING/COLUMN_COUNT_EXPORT", {
                count: unlockedVisibleCount,
              })
            : translate("QMS/COLUMN_MAPPING/FIELD_COUNT", {
                count: unlockedVisibleCount,
              })}
          {isExportMode && selectedCount ? ` · ${selectedCount} records` : ""}
          {!isExportMode && unlockedScopeIds.size > 1
            ? ` (${translate("QMS/COLUMN_MAPPING/SCOPE_COUNT", { count: unlockedScopeIds.size })})`
            : ""}
          {hasAnyRollup
            ? `, ${state.mappingRows.filter((m) => m.isRollup).length} ${translate("QMS/COLUMN_MAPPING/AGGREGATION_LABEL")}`
            : ""}
        </p>
        <button
          onClick={proceed}
          disabled={
            (!isExportMode && hasUnresolvedFlags) || unlockedVisibleCount === 0
          }
          className="flex items-center gap-1.5 rounded-lg bg-yellow1 px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-yellow5/30 disabled:opacity-40"
        >
          {isExportMode ? (
            <>
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
              {translate("QMS/INGESTION/PROCEED_BTN")}
            </>
          ) : (
            <>
              {translate("QMS/COLUMN_MAPPING/START_EXTRACTION_BTN", {
                count: unlockedVisibleCount,
                scopes:
                  unlockedScopeIds.size > 1
                    ? `, ${translate("QMS/COLUMN_MAPPING/SCOPE_COUNT", { count: unlockedScopeIds.size })}`
                    : "",
                agg: hasAnyRollup
                  ? `, ${state.mappingRows.filter((m) => m.isRollup).length} ${translate("QMS/COLUMN_MAPPING/AGG_ABBR")}`
                  : "",
              })}
            </>
          )}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// MappingTable — renders the column assignment table for one scope tab
// ---------------------------------------------------------------------------

function MappingTable({
  rows,
  headers,
  scopeId,
  effectiveTab,
  pkPrimitiveId,
  usedColumnIndices,
  scopeNames,
  dispatch,
  mode = "import",
  readOnly,
  onEditHelper,
}: {
  rows: MappingRow[]
  headers: string[]
  scopeId: string
  effectiveTab: string
  pkPrimitiveId: string | null
  usedColumnIndices: Set<number>
  scopeNames: Record<string, string>
  dispatch: React.Dispatch<Action>
  mode?: "import" | "export"
  readOnly?: boolean
  onEditHelper?: (primitiveId: string) => void
}) {
  const { translate } = useTranslation()
  const isExport = mode === "export"
  return (
    <div className="mb-3 max-h-[35vh] overflow-hidden overflow-y-auto rounded-lg border">
      <table className="w-full text-xs">
        <thead>
          <tr className="sticky top-0 border-b bg-grey1">
            <th className="w-10 px-3 py-1.5 text-left font-medium text-grey5">
              {translate("QMS/COLUMN_MAPPING/SHOW_HEADER")}
            </th>
            <th className="px-3 py-1.5 text-left font-medium text-grey5">
              {translate("QMS/COLUMN_MAPPING/FIELD_HEADER")}
            </th>
            <th className="px-3 py-1.5 text-left font-medium text-grey5">
              {isExport
                ? translate("QMS/COLUMN_MAPPING/COLUMN_NAME")
                : translate("QMS/COLUMN_MAPPING/FILE_COLUMN_HEADER")}
            </th>
            <th className="px-3 py-1.5 text-left font-medium text-grey5">
              {translate("QMS/INGESTION/SIMILARITY")}
            </th>
            <th
              className="w-14 px-3 py-1.5 text-center font-medium text-grey5"
              title={translate("QMS/INGESTION/AGGREGATION")}
            >
              {translate("QMS/COLUMN_MAPPING/AGG_ABBR")}
            </th>
            <th className="w-8"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-grey4/50">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={6} className="py-6 text-center text-xs text-grey5">
                {translate("QMS/INGESTION/NO_COLUMNS_MAPPED")}
              </td>
            </tr>
          ) : (
            rows.map((r) => {
              const isPk =
                pkPrimitiveId != null && r.primitive_id === pkPrimitiveId
              const isPrimaryPk = isPk && effectiveTab === scopeId
              const isRelation = r.primitive_type === "relation"
              const tc = r.typeCheck

              return (
                <tr
                  key={r.primitive_id}
                  className={clsx(
                    "transition-colors hover:bg-grey1",
                    isPrimaryPk && "bg-grey1/60",
                    !r.visible && "opacity-50",
                    r.disabled && "pointer-events-none opacity-40",
                  )}
                >
                  {/* Show/Hide */}
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={r.visible}
                      disabled={isPrimaryPk || r.disabled || readOnly}
                      onChange={() =>
                        dispatch({
                          type: "TOGGLE_VISIBLE",
                          primitiveId: r.primitive_id,
                        })
                      }
                      className="rounded accent-yellow1"
                    />
                  </td>
                  {/* Field name — same structure in both modes */}
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                      <span className="font-mono shrink-0 text-[10px] text-grey5">
                        [{r.primitive_type}]
                      </span>
                      <span className="truncate font-medium text-white">
                        {r.primitive_title}
                      </span>
                      {(isPrimaryPk || (isExport && r.is_pk)) && (
                        <span className="shrink-0 rounded bg-yellow1/15 px-1 py-0.5 text-[10px] font-medium text-yellow1">
                          PK
                        </span>
                      )}
                      {isRelation && r.related_scope_id && (
                        <span className="shrink-0 rounded bg-purple1/20 px-1 py-0.5 text-[10px] font-medium text-purple1">
                          →{" "}
                          {scopeNames[r.related_scope_id] ?? r.related_scope_id}
                        </span>
                      )}
                      {r.rollupConfig && (
                        <span className="shrink-0 rounded bg-purple1/20 px-1 py-0.5 text-[10px] font-medium text-purple1">
                          {translate("QMS/COLUMN_MAPPING/ROLLUP_BADGE")} ·{" "}
                          {scopeNames[r.rollupConfig.sourceScopeId] ??
                            r.rollupConfig.sourceScopeId}
                        </span>
                      )}
                      {r.isHelper && (
                        <span className="shrink-0 rounded bg-success/20 px-1 py-0.5 text-[10px] font-medium text-success">
                          {translate("QMS/HELPER/GROUP_LABEL")}
                        </span>
                      )}
                    </div>
                  </td>
                  {/* Column assignment: import = file column dropdown, export = editable column name */}
                  <td className="px-3 py-2">
                    {r.isHelper && r.helperConfig ? (
                      // Uses line-clamp-1 (not truncate) so the text stays
                      // wrappable for the table's auto-layout pass, which
                      // keeps the Helfer cell the same width as the regular
                      // "Dateispalte" select instead of demanding the full
                      // nowrap content width and squeezing out the Type check
                      // / remove columns.
                      <button
                        type="button"
                        disabled={readOnly}
                        onClick={() => onEditHelper?.(r.primitive_id)}
                        title={`${r.helperConfig.targetDocHeader ? `${translate("QMS/HELPER/DOC_COLUMN_TITLE")}: ${r.helperConfig.targetDocHeader}\n` : ""}${translate("QMS/HELPER/FORMULA_TITLE")}: ${r.helperConfig.expression}`}
                        className={clsx(
                          "font-mono flex w-full min-w-0 items-center gap-1.5 overflow-hidden rounded border px-2 py-1 text-left text-xs",
                          "border-success/40 bg-grey2 text-grey6 hover:bg-success/10",
                          readOnly && "cursor-not-allowed opacity-70",
                        )}
                      >
                        <svg
                          className="h-3 w-3 shrink-0 text-success"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                        <span className="line-clamp-1 min-w-0 flex-1 break-all">
                          {r.helperConfig.targetDocHeader ??
                            r.helperConfig.label}
                        </span>
                      </button>
                    ) : isExport ? (
                      <input
                        type="text"
                        value={r.label ?? r.primitive_title}
                        onChange={(e) =>
                          dispatch({
                            type: "RENAME_COLUMN",
                            primitiveId: r.primitive_id,
                            label: e.target.value,
                          })
                        }
                        disabled={readOnly}
                        className={clsx(
                          "w-full rounded border bg-grey2 px-2 py-1 text-xs text-grey6 focus:border-yellow1 focus:outline-hidden",
                          readOnly && "cursor-not-allowed opacity-70",
                        )}
                      />
                    ) : headers.length > 0 ? (
                      <select
                        value={r.column_index}
                        onChange={(e) => {
                          const colIdx = Number(e.target.value)
                          if (isPrimaryPk) {
                            dispatch({ type: "MAP_PK", columnIndex: colIdx })
                          } else if (isRelation && r.related_scope_id) {
                            if (colIdx < 0) {
                              dispatch({
                                type: "UNMAP_RELATION",
                                primitiveId: r.primitive_id,
                                targetScopeId: r.related_scope_id,
                              })
                            } else {
                              dispatch({
                                type: "MAP_RELATION",
                                primitiveId: r.primitive_id,
                                columnIndex: colIdx,
                                targetScopeId: r.related_scope_id,
                              })
                            }
                          } else {
                            dispatch({
                              type: "MAP_COLUMN",
                              primitiveId: r.primitive_id,
                              columnIndex: colIdx,
                            })
                          }
                        }}
                        className={clsx(
                          "font-mono w-full rounded-md border bg-grey2 px-1.5 py-1 text-xs focus:outline-hidden focus:ring-1 focus:ring-yellow1",
                          isPrimaryPk ? "border-yellow5/50" : "border-grey4",
                        )}
                      >
                        <option value={-1}>
                          {translate("QMS/COMMON/NONE_OPTION")}
                        </option>
                        {headers.map((h, idx) => (
                          <option
                            key={idx}
                            value={idx}
                            disabled={
                              usedColumnIndices.has(idx) &&
                              idx !== r.column_index
                            }
                          >
                            {h ||
                              translate("QMS/COLUMN_MAPPING/COLUMN_FALLBACK", {
                                index: idx + 1,
                              })}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-xs italic text-grey5">
                        {translate("QMS/INGESTION/NO_HEADERS")}
                      </span>
                    )}
                  </td>
                  {/* Type check: import = validation, export = type badge */}
                  <td className="px-3 py-2">
                    {isExport ? (
                      isRelation ? (
                        <span className="rounded bg-purple1/20 px-1.5 py-0.5 text-[10px] text-purple1">
                          {translate("QMS/INGESTION/RELATION_BADGE")}
                        </span>
                      ) : (
                        <span className="text-[10px] text-grey5">
                          {r.primitive_type}
                        </span>
                      )
                    ) : r.column_index < 0 ? (
                      <span className="text-[10px] text-grey5">—</span>
                    ) : isRelation ? (
                      <span className="rounded bg-purple1/20 px-1.5 py-0.5 text-[10px] text-purple1">
                        {translate("QMS/INGESTION/RELATION_BADGE")}
                      </span>
                    ) : tc === null ? (
                      <span className="text-[10px] text-grey5">
                        {translate("QMS/COMMON/LOADING")}
                      </span>
                    ) : tc.compatible ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-success">
                        <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-success/20 text-success">
                          <svg
                            className="h-2.5 w-2.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </span>
                        {translate("QMS/COLUMN_MAPPING/COMPATIBLE")}
                      </span>
                    ) : (
                      <div>
                        <span className="mb-1 inline-flex items-center gap-1 text-[10px] text-red1">
                          <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red1/20 font-bold text-red1">
                            !
                          </span>
                          {translate("QMS/COLUMN_MAPPING/INCOMPATIBLE_COUNT", {
                            count: tc.incompatibleCount,
                          })}
                        </span>
                        <div className="mb-1.5 flex flex-wrap gap-1">
                          {tc.incompatibleSamples.map((s, i) => (
                            <span
                              key={i}
                              className="font-mono max-w-[80px] truncate rounded bg-red1/10 px-1 text-[10px] text-red1"
                              title={s}
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                        <input
                          type="text"
                          value={r.cleaningInstruction}
                          onChange={(e) =>
                            dispatch({
                              type: "SET_CLEANING_INSTRUCTION",
                              primitiveId: r.primitive_id,
                              instruction: e.target.value,
                            })
                          }
                          placeholder={translate(
                            "QMS/COLUMN_MAPPING/CLEANING_INSTRUCTION_PLACEHOLDER",
                          )}
                          className="w-full rounded border border-red1/30 bg-red1/10 px-2 py-1 text-[11px] text-grey6 placeholder:text-red1 focus:border-red1 focus:outline-hidden"
                        />
                      </div>
                    )}
                  </td>
                  {/* Rollup / Aggregation */}
                  <td className="px-3 py-2 text-center">
                    {r.isHelper ? (
                      <span className="text-[10px] text-grey5">—</span>
                    ) : r.rollupConfig ? (
                      <select
                        value={r.rollupConfig.aggregation}
                        onChange={(e) =>
                          dispatch({
                            type: "SET_ROLLUP_AGGREGATION",
                            primitiveId: r.primitive_id,
                            aggregation: e.target.value as RollupAggregation,
                          })
                        }
                        disabled={readOnly}
                        className="rounded border border-purple1/30 bg-purple1/10 px-1 py-0.5 text-[10px] font-medium text-purple1 focus:outline-hidden focus:ring-1 focus:ring-purple1"
                      >
                        <option value="concat_unique">
                          {translate("QMS/COLUMN_MAPPING/AGG_UNIQUE")}
                        </option>
                        <option value="concat">
                          {translate("QMS/COLUMN_MAPPING/AGG_CONCAT")}
                        </option>
                        <option value="sum">
                          {translate("QMS/COLUMN_MAPPING/AGG_SUM")}
                        </option>
                        <option value="count">
                          {translate("QMS/COLUMN_MAPPING/AGG_COUNT")}
                        </option>
                      </select>
                    ) : r.primitive_scope_id !== effectiveTab ||
                      (isRelation && r.related_scope_id) ? (
                      <span className="text-[10px] text-grey5">—</span>
                    ) : r.primitive_scope_id !== scopeId ? (
                      <input
                        type="checkbox"
                        checked={!!r.isRollup}
                        onChange={() =>
                          dispatch({
                            type: "TOGGLE_ROLLUP",
                            primitiveId: r.primitive_id,
                          })
                        }
                        className="rounded accent-yellow1"
                        title={
                          r.isRollup
                            ? translate("QMS/INGESTION/ROLLUP_ACTIVE", {
                                type:
                                  r.primitive_type === "number"
                                    ? translate("QMS/INGESTION/ROLLUP_SUM")
                                    : translate("QMS/INGESTION/ROLLUP_UNIQUE"),
                              })
                            : translate("QMS/INGESTION/ROLLUP_ENABLE")
                        }
                      />
                    ) : (
                      <span className="text-[10px] text-grey5">—</span>
                    )}
                  </td>
                  {/* Remove */}
                  <td className="px-1 py-2 text-center">
                    {!isPrimaryPk && !readOnly && (
                      <button
                        onClick={() =>
                          dispatch({
                            type: "REMOVE_PRIMITIVE",
                            primitiveId: r.primitive_id,
                          })
                        }
                        className="text-grey5 transition-colors hover:text-red1"
                        title={translate("QMS/INGESTION/REMOVE")}
                      >
                        <svg
                          className="h-3.5 w-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </td>
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// SimpleMappingView — for single-record mode (no cross-scope)
// ---------------------------------------------------------------------------

function SimpleMappingView({
  scopeId,
  headers,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  rows,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  previewRows,
  mappingRows,
  pkPrimitiveId,
  pkPrimitiveTitle,
  pkValue,
  usedColumnIndices,
  tabAvailablePrims,
  detected,
  hasUnresolvedFlags,
  dispatch,
  onProceed,
}: {
  scopeId: string
  headers: string[]
  rows: string[][]
  previewRows: string[][]
  mappingRows: MappingRow[]
  pkPrimitiveId: string | null
  pkPrimitiveTitle: string
  pkValue?: string
  usedColumnIndices: Set<number>
  tabAvailablePrims: PrimitiveDef[]
  detected: DetectOut
  hasUnresolvedFlags: boolean
  dispatch: React.Dispatch<Action>
  onProceed: () => void
}) {
  const { translate } = useTranslation()
  const visibleCount = mappingRows.filter((m) => m.visible).length
  const [addDropdownOpen, setAddDropdownOpen] = useState(false)
  const [addSearch, setAddSearch] = useState("")
  const filtered = addSearch
    ? tabAvailablePrims.filter((p) =>
        p.title.toLowerCase().includes(addSearch.toLowerCase()),
      )
    : tabAvailablePrims

  return (
    <div>
      <p className="mb-4 text-sm text-grey5">
        {translate("QMS/COLUMN_MAPPING/MAP_COLUMNS_SINGLE_RECORD_DESC")}
      </p>

      {pkValue && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-grey4 bg-grey1 px-3 py-2">
          <span className="rounded bg-yellow1/15 px-1 py-0.5 text-[10px] font-medium text-yellow1">
            PK
          </span>
          <span className="text-sm font-medium text-white">
            {pkPrimitiveTitle}
          </span>
          <span className="font-mono text-sm text-grey5">{pkValue}</span>
        </div>
      )}

      <div className="flex items-center justify-between py-2">
        <div className="flex gap-2">
          <button
            onClick={() =>
              dispatch({ type: "TOGGLE_ALL_VISIBLE", scopeId, visible: true })
            }
            className="text-[10px] text-yellow1 hover:underline"
          >
            {translate("QMS/COLUMN_MAPPING/SHOW_ALL")}
          </button>
          <button
            onClick={() =>
              dispatch({ type: "TOGGLE_ALL_VISIBLE", scopeId, visible: false })
            }
            className="text-[10px] text-yellow1 hover:underline"
          >
            {translate("QMS/COLUMN_MAPPING/HIDE_ALL")}
          </button>
        </div>
        <div className="relative">
          <button
            onClick={() => setAddDropdownOpen((v) => !v)}
            className="flex items-center gap-1 rounded-lg border border-yellow5 px-2.5 py-1 text-[11px] font-medium text-yellow1 transition-colors hover:bg-yellow5/10"
          >
            <svg
              className="h-3 w-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
            {translate("QMS/INGESTION/ADD_COLUMN")}
          </button>
          {addDropdownOpen && (
            <div className="absolute right-0 top-full z-10 mt-1 w-72 overflow-hidden rounded-lg border border-grey4 bg-grey2 shadow-lg">
              <div className="border-b p-2">
                <input
                  type="text"
                  value={addSearch}
                  onChange={(e) => setAddSearch(e.target.value)}
                  placeholder={translate(
                    "QMS/INGESTION/SEARCH_FIELDS_PLACEHOLDER",
                  )}
                  className="w-full rounded-md border border-grey4 px-2.5 py-1.5 text-xs focus:outline-hidden focus:ring-1 focus:ring-yellow1"
                  autoFocus
                />
              </div>
              <div className="max-h-48 overflow-y-auto">
                {filtered.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-grey5">
                    {tabAvailablePrims.length === 0
                      ? translate("QMS/INGESTION/ALL_COLUMNS_ADDED")
                      : translate("QMS/COMMON/NO_RESULTS")}
                  </p>
                ) : (
                  filtered.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => {
                        dispatch({
                          type: "ADD_PRIMITIVE",
                          primitive: p,
                          detected,
                        })
                        setAddDropdownOpen(false)
                        setAddSearch("")
                      }}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-xs transition-colors hover:bg-grey1"
                    >
                      <span>
                        <span className="font-medium text-white">
                          {p.title}
                        </span>
                        <span className="font-mono ml-1.5 text-[10px] text-grey5">
                          [{p.type}]
                        </span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <MappingTable
        rows={mappingRows}
        headers={headers}
        scopeId={scopeId}
        effectiveTab={scopeId}
        pkPrimitiveId={pkPrimitiveId}
        usedColumnIndices={usedColumnIndices}
        scopeNames={{}}
        dispatch={dispatch}
      />

      {hasUnresolvedFlags && (
        <div className="mb-3 rounded-lg border border-warning bg-warning/10 p-2.5 text-xs text-warning">
          {translate("QMS/INGESTION/FIELDS_SHOWN")}
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-xs text-grey5">
          {translate("QMS/COLUMN_MAPPING/FIELDS_MAPPED", {
            count: visibleCount,
          })}
        </p>
        <button
          onClick={onProceed}
          disabled={hasUnresolvedFlags || visibleCount === 0}
          className="rounded-lg bg-yellow1 px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-yellow5/30 disabled:opacity-40"
        >
          {translate("QMS/COLUMN_MAPPING/START_EXTRACTION_SIMPLE_BTN", {
            count: visibleCount,
          })}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// DiffPreview — shows document vs database values for the first few rows
// ---------------------------------------------------------------------------

/**
 * Content fingerprint of a whole sheet, for query keys: every header and cell
 * counts, so two files only share a key when they are the same request. Two
 * independent 32-bit FNV-1a style mixes (the scheme `fingerprintIds` uses in
 * material-compliance) make a collision about 2^-64, and the key stays a
 * short string instead of the arrays TanStack would re-hash on every render.
 */
function fingerprintSheet(
  headers: readonly string[],
  rows: readonly (readonly string[])[],
): string {
  let h1 = 0x811c9dc5
  let h2 = 0xcbf29ce4
  const mix = (code: number) => {
    h1 = Math.imul(h1 ^ code, 0x01000193)
    h2 = Math.imul(h2 ^ code, 0x85ebca6b)
    h2 = (h2 << 13) | (h2 >>> 19)
  }
  for (const row of [headers, ...rows]) {
    for (const cell of row) {
      // Parsed rows can be sparse or carry non-strings despite the type.
      const text = String(cell ?? "")
      for (let i = 0; i < text.length; i++) mix(text.charCodeAt(i))
      mix(0x1f) // unit separator: end of cell
    }
    mix(0x1e) // record separator: end of row
  }
  return `${rows.length}:${(h1 >>> 0).toString(36)}:${(h2 >>> 0).toString(36)}`
}

export function DiffPreview({
  previewRows,
  mappingRows,
  headers,
  scopeId,
  pkPrimitiveId,
  anchorMode = "pk",
  anchorMappingPrimitiveId = null,
  anchorMappingColumnIndex = -1,
  anchorMappingTitle = "",
  allRows,
  allHeaders,
}: {
  previewRows: string[][]
  mappingRows: MappingRow[]
  headers: string[]
  scopeId: string
  pkPrimitiveId: string | null
  /** When 'mapping', preview resolves rows via the anchor API's pair matches
   *  instead of scope PK lookup. The PK column is hidden from the preview. */
  anchorMode?: AnchorMode
  anchorMappingPrimitiveId?: string | null
  anchorMappingColumnIndex?: number
  /** User-facing label for the anchor column in mapping mode — the mapping's
   *  configured title (e.g. "Fremdartikelnummer"), not the relation
   *  primitive's title (which is typically the related scope's name). */
  anchorMappingTitle?: string
  /** Full row set and headers, used to run the anchor API once in mapping
   *  mode to populate previewRecordIds. */
  allRows?: string[][]
  allHeaders?: string[]
}) {
  const { translate } = useTranslation()
  const qc = useQueryClient()
  const visibleMappings = mappingRows.filter((r) => r.visible && !r.disabled)

  const pkMapping = pkPrimitiveId
    ? mappingRows.find((r) => r.primitive_id === pkPrimitiveId)
    : null
  const pkColIdx = pkMapping?.column_index ?? -1

  // --- Mapping-anchor preview lookup ---
  // Run the anchor API once whenever the mapping column / primitive changes so
  // the preview can resolve each row to its pair's primary record id.  The
  // call is permissive: any unresolved / ambiguous row falls back to "no
  // match" silently so the preview doesn't blow up while the user is still
  // configuring things.
  const isMappingAnchor =
    anchorMode === "mapping" &&
    !!anchorMappingPrimitiveId &&
    anchorMappingColumnIndex >= 0 &&
    !!allRows &&
    !!allHeaders
  // Memoized on the arrays, so a sheet is hashed once rather than per render.
  const sheetFingerprint = useMemo(
    () =>
      allRows && allHeaders && isMappingAnchor
        ? fingerprintSheet(allHeaders, allRows)
        : "",
    [isMappingAnchor, allHeaders, allRows],
  )
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- allRows/allHeaders are keyed by sheetFingerprint, their content hash, instead of the arrays themselves
  const mappingAnchorQuery = useQuery({
    queryKey: [
      "preview-anchor-mapping",
      scopeId,
      anchorMappingPrimitiveId,
      anchorMappingColumnIndex,
      sheetFingerprint,
    ],
    enabled: isMappingAnchor,
    queryFn: async () => {
      try {
        const out = await api.ingestAnchor(scopeId, {
          file_type: "excel",
          rows: allRows,
          headers: allHeaders,
          anchor_mode: "mapping",
          mapping_primitive_id: anchorMappingPrimitiveId!,
          mapping_col_override: anchorMappingColumnIndex,
        })
        const rowToRecord = new Map<number, string>()
        for (const m of out.matches) {
          if (m.row_index != null) rowToRecord.set(m.row_index, m.record_id)
        }
        return rowToRecord
      } catch {
        // Unresolved / ambiguous row errors — the full file isn't clean yet.
        // Preview should degrade gracefully; extraction step will surface
        // the actual error.
        return new Map<number, string>()
      }
    },
    staleTime: 10_000,
  })
  const mappingRowToRecord =
    mappingAnchorQuery.data ?? new Map<number, string>()

  const cached = qc.getQueryData<{
    records: {
      id: string
      values: Record<string, { value: string | null }>
      relation_tags?: {
        primitive_id: string
        primitive_name: string
        related_scope_id: string
        count: number
      }[]
    }[]
    scope: { primitives: { id: string; name: string; is_pk: boolean }[] }
  }>(["records", scopeId])

  const pkPrim = cached?.scope.primitives.find((p) => p.is_pk)
  const pkToRecord = new Map<
    string,
    {
      id: string
      values: Record<string, { value: string | null }>
      relation_tags?: {
        primitive_id: string
        primitive_name: string
        related_scope_id: string
        count: number
      }[]
    }
  >()
  if (pkPrim && cached) {
    for (const rec of cached.records) {
      const pkVal = rec.values[pkPrim.name]?.value
      if (pkVal) pkToRecord.set(pkVal.trim().toLowerCase(), rec)
    }
  }

  const primIdToName = new Map<string, string>()
  if (cached) {
    for (const p of cached.scope.primitives) primIdToName.set(p.id, p.name)
  }

  // Resolve the global row index of each preview row so mapping-mode pair
  // matches can be looked up by their actual row_index (matches the index
  // used in the anchor API response).
  const previewRowIndices = previewRows.map((_row, i) => i)

  const previewRecordIds = previewRows.map((row, i) => {
    if (isMappingAnchor) {
      return mappingRowToRecord.get(previewRowIndices[i]) ?? null
    }
    const docPk = pkColIdx >= 0 ? (row[pkColIdx] ?? "").trim() : ""
    return docPk ? (pkToRecord.get(docPk.toLowerCase())?.id ?? null) : null
  })

  const rollupMappings = visibleMappings.filter(
    (r) => r.primitive_scope_id !== scopeId,
  )
  const validRecordIds = previewRecordIds.filter(
    (id): id is string => id !== null,
  )

  const [rollupValues, setRollupValues] = useState<
    Record<string, Record<string, string | null>>
  >({})

  const rollupKey = rollupMappings
    .map((m) => m.primitive_id)
    .sort()
    .join(",")
  const recordKey = validRecordIds.sort().join(",")

  useEffect(() => {
    if (rollupMappings.length === 0 || validRecordIds.length === 0) {
      setRollupValues({})
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const result = await api.computeRollup(scopeId, {
          record_ids: validRecordIds,
          columns: rollupMappings.map((m) => ({
            primitive_id: m.primitive_id,
            primitive_scope_id: m.primitive_scope_id,
            aggregation:
              m.primitive_type === "number" ? "sum" : "concat_unique",
            label: m.primitive_title,
          })),
        })
        if (cancelled) return
        const vals: Record<string, Record<string, string | null>> = {}
        for (const v of result.values) {
          if (!vals[v.record_id]) vals[v.record_id] = {}
          vals[v.record_id][v.primitive_id] = v.value
        }
        setRollupValues(vals)
      } catch (e) {
        console.warn("Rollup preview fetch failed:", e)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [rollupKey, recordKey])

  function getDbValue(
    r: MappingRow,
    matchedRecord: typeof pkToRecord extends Map<string, infer V> ? V : never,
    recordId: string | null,
    docVal: string,
  ): string {
    // Mapping-anchor row: DB "value" is the mapped_id on the edge.  If the
    // anchor query resolved this row to a record, the edge exists and carries
    // the doc value as mapped_id — surface that so the preview shows a match.
    if (isMappingAnchor && r.primitive_id === anchorMappingPrimitiveId) {
      return recordId ? docVal : ""
    }
    if (r.primitive_type === "relation" && matchedRecord?.relation_tags) {
      const tag = matchedRecord.relation_tags.find(
        (t) => t.primitive_id === r.primitive_id,
      )
      return tag
        ? translate("QMS/INGESTION/RECORDS_MATCHED", {
            count: tag.count,
          })
        : ""
    }
    if (r.primitive_scope_id === scopeId) {
      const primName = primIdToName.get(r.primitive_id)
      const rec =
        matchedRecord ??
        (recordId ? cached?.records.find((x) => x.id === recordId) : undefined)
      return (rec && primName ? rec.values[primName]?.value : null) ?? ""
    }
    if (recordId) return rollupValues[recordId]?.[r.primitive_id] ?? ""
    return ""
  }

  /** Evaluate helper rows for ONE preview row.  Helpers are topo-sorted by
   *  dependency on each other's labels so expressions like `total = qty * price`
   *  then `tax = total * 0.19` work without asking the user to order them. */
  function computeHelperValues(
    helpers: MappingRow[],
    row: string[],
    matchedRecord: typeof pkToRecord extends Map<string, infer V> ? V : never,
    recordId: string | null,
  ): Record<string, { value: string | null; error: string | null }> {
    const result: Record<
      string,
      { value: string | null; error: string | null }
    > = {}
    const labelToHelper = new Map<string, MappingRow>()
    for (const h of helpers) {
      if (h.helperConfig?.label) labelToHelper.set(h.helperConfig.label, h)
    }
    // Topo sort: iterate up to N rounds; each round resolves any helper whose
    // dependencies are already computed.  Cycles are surfaced as errors on the
    // remaining unresolved helpers.
    const resolvedLabels = new Set<string>()
    let changed = true
    while (changed) {
      changed = false
      for (const h of helpers) {
        if (!h.helperConfig) continue
        const label = h.helperConfig.label
        if (resolvedLabels.has(label)) continue
        const refs = extractReferencesForEval(h.helperConfig.expression)
        const blockedByHelper = refs.identifiers.some(
          (ident) =>
            labelToHelper.has(ident) &&
            !resolvedLabels.has(ident) &&
            ident !== label,
        )
        if (blockedByHelper) continue
        const identifiers: Record<string, string> = {}
        for (const ident of refs.identifiers) {
          if (labelToHelper.has(ident)) {
            identifiers[ident] =
              result[labelToHelper.get(ident)!.primitive_id]?.value ?? ""
          } else {
            // Primitive lookup by name (same scope).  Rollups use the label
            // substring as identifier, handled separately below.
            const primId = [...primIdToName.entries()].find(
              ([, name]) => name === ident,
            )?.[0]
            if (primId) {
              const rec =
                matchedRecord ??
                (recordId
                  ? cached?.records.find((x) => x.id === recordId)
                  : undefined)
              identifiers[ident] = (rec ? rec.values[ident]?.value : null) ?? ""
            } else {
              // Maybe a rollup label slugified: match against rollup rows.
              const rollupRow = helpers.find(
                (r) =>
                  r.rollupConfig &&
                  r.label &&
                  r.label.replace(/[^\w]/g, "_") === ident,
              )
              if (rollupRow && recordId) {
                identifiers[ident] =
                  rollupValues[recordId]?.[rollupRow.primitive_id] ?? ""
              } else {
                identifiers[ident] = ""
              }
            }
          }
        }
        const docCells: Record<string, string> = {}
        for (const hdr of refs.docHeaders) {
          const idx = headers.indexOf(hdr)
          docCells[hdr] = idx >= 0 ? (row[idx] ?? "").trim() : ""
        }
        result[h.primitive_id] = evalHelper(
          h.helperConfig.expression,
          { identifiers, docCells },
          translate,
        )
        resolvedLabels.add(label)
        changed = true
      }
    }
    // Any helper we couldn't resolve = cycle or missing dep.
    for (const h of helpers) {
      if (!result[h.primitive_id]) {
        result[h.primitive_id] = {
          value: null,
          error: translate("QMS/HELPER/CIRCULAR_REFERENCE_ERROR"),
        }
      }
    }
    return result
  }

  // Only show primary scope mappings in preview (secondary scope previews are complex).
  // In mapping-anchor mode, hide the PK row — the anchor column replaces it
  // as the identity key of the preview table.  Also order rows so the active
  // anchor is ALWAYS first: PK first in PK mode, mapping row first in mapping
  // mode.  This keeps the preview's leftmost column in lockstep with the
  // AnchorBox selection regardless of user editing order.
  const scopeRows = visibleMappings.filter(
    (r) => r.primitive_scope_id === scopeId,
  )
  const anchorRowId = isMappingAnchor ? anchorMappingPrimitiveId : pkPrimitiveId
  const anchorRow = anchorRowId
    ? scopeRows.find((r) => r.primitive_id === anchorRowId)
    : null
  const otherRows = scopeRows.filter((r) => {
    if (isMappingAnchor && r.primitive_id === pkPrimitiveId) return false
    if (r.primitive_id === anchorRowId) return false
    return true
  })
  const primaryVisibleMappings: MappingRow[] = anchorRow
    ? [anchorRow, ...otherRows]
    : otherRows
  if (primaryVisibleMappings.length === 0) return null

  const headerLabelFor = (r: MappingRow): string => {
    if (
      isMappingAnchor &&
      r.primitive_id === anchorMappingPrimitiveId &&
      anchorMappingTitle
    ) {
      return anchorMappingTitle
    }
    return r.primitive_title
  }

  return (
    <div className="mb-4">
      <p className="mb-1.5 text-xs text-grey5">
        {translate("QMS/INGESTION/PREVIEW_ROWS", { count: previewRows.length })}
      </p>
      <div className="overflow-x-auto rounded border text-xs">
        <table className="min-w-full">
          <thead>
            <tr className="bg-grey1">
              {primaryVisibleMappings.map((r) => (
                <th
                  key={r.primitive_id}
                  className="whitespace-nowrap border-r px-2 py-1.5 text-left font-medium text-grey5 last:border-r-0"
                >
                  {headerLabelFor(r)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {previewRows.map((row, ri) => {
              const docPk = pkColIdx >= 0 ? (row[pkColIdx] ?? "").trim() : ""
              const matchedRecord = docPk
                ? pkToRecord.get(docPk.toLowerCase())
                : undefined
              const recordId = previewRecordIds[ri]
              // Compute helper values up-front so downstream cells can display them.
              const helperRows = primaryVisibleMappings.filter(
                (r) => r.isHelper,
              )
              const helperResults =
                helperRows.length > 0
                  ? computeHelperValues(
                      helperRows,
                      row,
                      matchedRecord!,
                      recordId,
                    )
                  : {}

              return (
                <tr key={ri} className="border-t">
                  {primaryVisibleMappings.map((r) => {
                    if (r.isHelper) {
                      const res = helperResults[r.primitive_id]
                      return (
                        <td
                          key={r.primitive_id}
                          className="max-w-[200px] border-r px-2 py-1.5 last:border-r-0"
                        >
                          {res?.error ? (
                            <span
                              className="text-[11px] italic text-red1"
                              title={res.error}
                            >
                              — ({res.error})
                            </span>
                          ) : res?.value != null ? (
                            <span className="font-mono text-[11px] text-success">
                              {res.value}
                            </span>
                          ) : (
                            <span className="text-[11px] text-grey5">—</span>
                          )}
                        </td>
                      )
                    }
                    const docVal =
                      r.column_index >= 0
                        ? (row[r.column_index] ?? "").trim()
                        : ""
                    const dbVal = getDbValue(
                      r,
                      matchedRecord!,
                      recordId,
                      docVal,
                    )
                    const isPk =
                      pkPrimitiveId != null && r.primitive_id === pkPrimitiveId
                    const isAnchorKey =
                      isMappingAnchor &&
                      r.primitive_id === anchorMappingPrimitiveId
                    const docNorm = docVal.toLowerCase()
                    const dbNorm = dbVal.toLowerCase()
                    const match = docNorm === dbNorm && docVal !== ""

                    return (
                      <td
                        key={r.primitive_id}
                        className="max-w-[200px] border-r px-2 py-1.5 last:border-r-0"
                      >
                        {isPk || isAnchorKey ? (
                          <span className="font-mono text-[11px] text-white">
                            {docVal || <span className="text-grey5">--</span>}
                          </span>
                        ) : match ? (
                          <span className="text-[11px] text-grey5">
                            {docVal}
                          </span>
                        ) : (
                          <div className="flex flex-col gap-0.5">
                            <span
                              className={clsx(
                                "truncate text-[11px]",
                                !docVal ? "italic text-grey5" : "text-grey5",
                              )}
                              title={
                                docVal ||
                                translate("QMS/INGESTION/EMPTY_IN_DOCUMENT")
                              }
                            >
                              <span className="mr-1 text-[9px] text-grey5">
                                {translate("QMS/INGESTION/DOC_LABEL_ABBR")}
                              </span>
                              {docVal || "--"}
                            </span>
                            <span
                              className={clsx(
                                "truncate text-[11px]",
                                !dbVal ? "italic text-grey5" : "text-yellow1",
                              )}
                              title={
                                dbVal ||
                                translate("QMS/INGESTION/EMPTY_IN_DATABASE")
                              }
                            >
                              <span className="mr-1 text-[9px] text-grey5">
                                DB
                              </span>
                              {dbVal || "--"}
                            </span>
                          </div>
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ExportPreview — shows DB values for the first few selected records
// ---------------------------------------------------------------------------

function ExportPreview({
  scopeId,
  mappingRows,
  selectedRecordIds,
}: {
  scopeId: string
  mappingRows: MappingRow[]
  selectedRecordIds?: Set<string>
}) {
  const { translate } = useTranslation()
  const qc = useQueryClient()

  const cached = qc.getQueryData<{
    records: { id: string; values: Record<string, { value: string | null }> }[]
    scope: { primitives: { id: string; name: string; is_pk: boolean }[] }
  }>(["records", scopeId])

  if (!cached || mappingRows.length === 0) return null

  const primIdToName = new Map<string, string>()
  for (const p of cached.scope.primitives) primIdToName.set(p.id, p.name)

  // Get first 3 records from selected set (or all records if no selection)
  const records =
    selectedRecordIds && selectedRecordIds.size > 0
      ? cached.records.filter((r) => selectedRecordIds.has(r.id)).slice(0, 3)
      : cached.records.slice(0, 3)

  if (records.length === 0) return null

  return (
    <div className="mb-4">
      <p className="mb-1.5 text-xs text-grey5">
        {translate("QMS/INGESTION/RECORDS_MATCHED", {
          count: records.length,
        })}{" "}
        {translate("QMS/COLUMN_MAPPING/EXPORT_PREVIEW_FIRST_N", {
          count: records.length,
        })}
      </p>
      <div className="overflow-x-auto rounded border text-xs">
        <table className="min-w-full">
          <thead>
            <tr className="bg-grey1">
              {mappingRows.map((r) => (
                <th
                  key={r.primitive_id}
                  className="whitespace-nowrap border-r px-2 py-1.5 text-left font-medium text-grey5 last:border-r-0"
                >
                  {r.label ?? r.primitive_title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {records.map((rec) => (
              <tr key={rec.id} className="border-t">
                {mappingRows.map((r) => {
                  const primName = primIdToName.get(r.primitive_id)
                  const val = primName
                    ? (rec.values[primName]?.value ?? "")
                    : ""
                  return (
                    <td
                      key={r.primitive_id}
                      className="max-w-[200px] truncate border-r px-2 py-1.5 text-[11px] text-grey5 last:border-r-0"
                    >
                      {val || <span className="italic">--</span>}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
